import * as i0 from '@angular/core';
import { Component, HostListener, NgModule, Injectable, PLATFORM_ID, Inject, InjectionToken, Optional, EventEmitter, ViewChild, Input, Output, APP_INITIALIZER, ContentChildren, HostBinding } from '@angular/core';
import * as i1$1 from '@simpl/element-ng';
import { EditDiscardDialogResult, DeleteConfirmationDialogResult, SiToastNotificationService, SI_DATATABLE_CONFIG, SiDatatableModule, SiModalModule as SiModalModule$1, SiSearchBarModule, SiLocaleStore, SiInlineNotificationModule, SiLoadingSpinnerModule, SiNavbarModule, SiNavbarVerticalModule, SiThemeService, BOOTSTRAP_BREAKPOINTS, SiFormModule, SiResizeObserverModule, AlertDialogResult, SiAboutModule } from '@simpl/element-ng';
import * as i1 from 'rxjs';
import { BehaviorSubject, combineLatest, Subject, of, ReplaySubject } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, exhaustMap, first, switchMap, catchError } from 'rxjs/operators';
import * as i3 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i2 from '@angular/common';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as i1$4 from '@sisuite/auth';
import { SiOAuthService } from '@sisuite/auth';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import * as i3$1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i2$1 from '@siemens/ngx-datatable';
import { SelectionType, NgxDatatableModule } from '@siemens/ngx-datatable';
import * as i1$3 from 'ngx-cookie-service';
import * as i3$2 from 'angular-oauth2-oidc';

class SiConfigProvider {
}

/*
Known Http Error Codes.
Provide appropriate Translation in the json files
*/
const KNOWN_ERROR_CODES = [
    400,
    401,
    404,
    424,
    500,
    502,
    504,
    0
];

const siFormCanDeactivateGuard = (component) => {
    if (component.hasChanges()) {
        return component
            .cancel()
            .pipe(map(result => result !== EditDiscardDialogResult.Cancel));
    }
    return true;
};

var ViewMode;
(function (ViewMode) {
    ViewMode[ViewMode["View"] = 0] = "View";
    ViewMode[ViewMode["Edit"] = 1] = "Edit";
    ViewMode[ViewMode["Add"] = 2] = "Add";
})(ViewMode || (ViewMode = {}));

class PageCache {
}
const PAGE_LOAD_DEBOUNCE = 50;
class SiAbstractCrudComponent {
    beforeUnloadHandler(event) {
        if (this.hasChanges()) {
            event.returnValue = 'preventLoad';
            return false;
        }
        return true;
    }
    constructor(viewMode$, pageInfo$, siModal, router, route, pageCache$) {
        this.viewMode$ = viewMode$;
        this.pageInfo$ = pageInfo$;
        this.siModal = siModal;
        this.router = router;
        this.route = route;
        this.pageCache$ = pageCache$;
        this.labelWidth = 140;
        this.viewMode = ViewMode.View;
        this.searchTerm = '';
        this.selectedEntities = [];
        this.detailsActive = false;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.OPERATION_WAIT_THRESHOLD = 500;
        this.subscriptions = [];
        this.pageRequest$ = new BehaviorSubject({
            pageNumber: 0,
            pageSize: 50,
            search: ''
        });
        /**
         * Stable is the state if we have a selected query param id and loaded entities.
         * We need to track to auto-select the query param id of the first entity.
         *
         * - If the state is not stable we select the first element when the id is not set.
         * - If the state is stable and the query param id switches to undefined we cannot
         *   select the first entity. This case happens on filter change and olds still the
         *   old data
         */
        this.isStable = false;
    }
    ngOnInit() {
        this.subscriptions.push(this.viewMode$.subscribe(newViewMode => this.onViewModeChange(newViewMode)), this.pageRequest$
            .pipe(debounceTime(PAGE_LOAD_DEBOUNCE))
            .subscribe(pageRequest => this.checkLoadEntities(pageRequest)), combineLatest([this.route.queryParams, this.entities$])
            .pipe(distinctUntilChanged())
            .subscribe(([queryParams, entities]) => {
            const selectedIdChanged = this.queryParamsId !== queryParams.id;
            this.queryParamsId = queryParams?.id || undefined;
            // Load selected entity in details view
            if (selectedIdChanged && this.queryParamsId) {
                this.loadEntity(this.queryParamsId);
            }
            if (entities?.[0]) {
                if (!this.queryParamsId && !this.isStable) {
                    this.setSelectedEntityId(this.getEntityId(entities[0]));
                }
                if (this.queryParamsId) {
                    const selected = entities.find(entity => !!entity && this.getEntityId(entity) === this.queryParamsId);
                    if (selected) {
                        this.selectedEntities = [selected];
                    }
                }
                this.detailsActive = false;
            }
            if (queryParams.searchTerm &&
                queryParams.searchTerm !== this.searchTerm) {
                this.searchTerm = queryParams.searchTerm;
            }
            if (!queryParams.searchTerm || queryParams.searchTerm === '') {
                this.searchTerm = '';
            }
            this.isStable =
                !!this.queryParamsId && !!entities && entities?.length > 0;
        }));
        if (this.pageCache$) {
            this.subscriptions.push(this.pageCache$.subscribe(cache => (this.pageCache = cache)));
        }
        if (this.pageInfo$) {
            this.subscriptions.push(this.pageInfo$.subscribe(pageInfo => (this.pageInfo = pageInfo)));
        }
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
        if (this.viewMode !== ViewMode.View) {
            this.setViewMode(ViewMode.View);
        }
        this.unselectEntity();
    }
    onViewModeChange(viewMode) {
        this.form?.markAsUntouched();
        this.viewMode = viewMode || ViewMode.View;
        if (this.viewMode === ViewMode.Add) {
            this.detailsActive = true;
            this.form?.reset();
        }
        if (this.viewMode === ViewMode.View) {
            this.form?.reset();
            if (this.entity) {
                this.updateForm(this.entity);
            }
            else {
                this.detailsActive = false;
            }
            if (!this.selectedEntities || this.selectedEntities.length === 0) {
                this.detailsActive = false;
            }
        }
    }
    setFilter(filter) {
        const currentPageRequest = this.pageRequest$.value;
        this.clearPageCache();
        this.setPage({
            pageNumber: 0,
            pageSize: currentPageRequest.pageSize,
            search: currentPageRequest.search,
            filter
        });
    }
    setSearch(query) {
        let theSearch = '';
        if (query) {
            theSearch = query.trim();
        }
        if (theSearch && theSearch.length === 0) {
            theSearch = '';
        }
        this.clearPageCache();
        this.setPage({
            ...this.pageRequest$.value,
            pageNumber: 0,
            search: theSearch
        });
    }
    setPage(pageRequest) {
        this.pageRequest$.next(pageRequest);
    }
    clearSearchParams() {
        const params = { ...this.route.snapshot.queryParams };
        delete params.searchTerm;
        this.router.navigate([], { queryParams: params });
    }
    updateSearchParams(theSearch) {
        if (theSearch !== '') {
            const queryParams = { searchTerm: theSearch };
            this.router.navigate([], {
                queryParams,
                queryParamsHandling: 'merge'
            });
        }
        else {
            this.clearSearchParams();
        }
    }
    clearIdParams() {
        const params = { ...this.route.snapshot.queryParams };
        delete params.id;
        this.router.navigate([], { queryParams: params });
    }
    selectEntities(entities) {
        if (this.viewMode !== ViewMode.View) {
            this.setViewMode(ViewMode.View);
        }
        if (entities && entities.length > 0 && entities[0]) {
            this.setSelectedEntityId(this.getEntityId(entities[0]));
            this.selectedEntities = entities;
            this.detailsActive = true;
        }
    }
    setSelectedEntityId(id) {
        if (id !== this.queryParamsId) {
            const queryParams = { id };
            this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
        }
    }
    add() {
        this.setViewMode(ViewMode.Add);
    }
    edit() {
        this.setViewMode(ViewMode.Edit);
    }
    save() {
        if (!this.isValid()) {
            return;
        }
        if (this.isEditMode) {
            this.updateEntity();
        }
        else if (this.isAddMode) {
            this.addEntity();
        }
    }
    cancel() {
        const myResult = new Subject();
        if (!this.hasChanges()) {
            this.updateForm(this.entity);
            this.setViewMode(ViewMode.View);
            myResult.next(EditDiscardDialogResult.Discard);
            myResult.complete();
        }
        else {
            this.siModal
                .showEditDiscardDialog(!this.isValid(), 'SISUITE.EDIT_ABORT_DIALOG.MESSAGE', undefined, 'SISUITE.EDIT_ABORT_DIALOG.SAVE', 'SISUITE.EDIT_ABORT_DIALOG.DONT_SAVE', 'SISUITE.EDIT_ABORT_DIALOG.CANCEL', 'SISUITE.EDIT_ABORT_DIALOG.DISABLED_SAVE_MESSAGE', 'SISUITE.EDIT_ABORT_DIALOG.DISABLED_SAVE_ABORT')
                .subscribe(result => {
                if (result === EditDiscardDialogResult.Discard) {
                    this.updateForm(this.entity);
                    this.setViewMode(ViewMode.View);
                }
                else if (result === EditDiscardDialogResult.Save) {
                    this.save();
                }
                else if (result === EditDiscardDialogResult.Cancel) {
                    // do nothing
                }
                myResult.next(result);
                myResult.complete();
            });
            this.makeBlur();
            this.controlFlickering();
        }
        return myResult;
    }
    delete(message, heading, deleteBtnName = 'SISUITE.COMMON.DELETE', cancelBtnName = 'SISUITE.COMMON.CANCEL', translationParams) {
        this.siModal
            .showDeleteConfirmationDialog(message, heading, deleteBtnName, cancelBtnName, translationParams)
            .subscribe(result => {
            switch (result) {
                case DeleteConfirmationDialogResult.Delete:
                    this.deleteEntity();
                    // set back to view mode afterwards
                    this.viewMode = ViewMode.View;
                    break;
                case DeleteConfirmationDialogResult.Cancel:
                    break;
            }
        });
        this.controlFlickering();
    }
    resetForm() {
        if (this.form) {
            this.form.reset();
        }
    }
    getField(fieldName) {
        if (!this.form) {
            return null;
        }
        return this.form.get(fieldName);
    }
    getFieldValue(fieldName) {
        const field = this.getField(fieldName);
        if (field) {
            return field.value;
        }
    }
    setFieldValue(fieldName, fieldValue) {
        const field = typeof fieldName === 'string' ? this.getField(fieldName) : fieldName;
        if (field) {
            field.setValue(fieldValue);
        }
    }
    isFieldRequired(fieldName) {
        const formField = this.getField(fieldName);
        if (!formField?.validator) {
            return false;
        }
        const validator = formField.validator({});
        return validator?.required || false;
    }
    get isAddMode() {
        return this.viewMode === ViewMode.Add;
    }
    get isEditMode() {
        return this.viewMode === ViewMode.Edit;
    }
    get readonly() {
        return this.viewMode === ViewMode.View;
    }
    isValid() {
        return this.form ? this.form.valid : true;
    }
    hasChanges() {
        if (!this.form) {
            return false;
        }
        else {
            return !this.form.pristine;
        }
    }
    checkLoadEntities(pageRequest) {
        if (!this.isPagedCached(pageRequest.pageNumber)) {
            this.loadEntities(pageRequest);
        }
        this.updateSearchParams(pageRequest.search);
        const nextPageNumber = pageRequest.pageNumber + 1;
        const hasNextPage = this.pageInfo
            ? nextPageNumber < this.pageInfo?.totalPages
            : false;
        if (hasNextPage && !this.isPagedCached(nextPageNumber)) {
            this.loadEntities({
                ...pageRequest,
                pageNumber: nextPageNumber
            });
        }
        const previousPageNumber = pageRequest.pageNumber - 1;
        if (previousPageNumber > 0 && !this.isPagedCached(previousPageNumber)) {
            this.loadEntities({
                ...pageRequest,
                pageNumber: previousPageNumber
            });
        }
    }
    isPagedCached(pageNumber) {
        return this.pageCache ? !!this.pageCache[pageNumber] : false;
    }
    onEntityChange(newEntity) {
        if (!newEntity) {
            // If the selected entity is deleted we clear the params
            // to start the first selection flow. But in case of a first
            // deep link, we should not do this. We identify this on the
            // entity, which is undefined initially.
            if (this.entity) {
                this.clearIdParams();
            }
            this.entity = undefined;
            this.detailsActive = false;
        }
        else {
            this.entity = newEntity;
            this.setSelectedEntityId(this.getEntityId(this.entity));
            if (this.viewMode === undefined || this.viewMode === ViewMode.View) {
                this.updateForm(this.entity);
            }
            else {
                if (!this.hasChanges()) {
                    this.updateForm(this.entity);
                }
            }
        }
    }
    setViewMode(viewMode) {
        throw new Error('Not implemented!');
    }
    loadEntities(pageRequest) {
        throw new Error('Not implemented!');
    }
    loadEntity(id) {
        throw new Error('Not implemented!');
    }
    clearPageCache() {
        throw new Error('Not implemented!');
    }
    updateForm(entity) {
        throw new Error('Not implemented!');
    }
    addEntity() {
        throw new Error('Not implemented!');
    }
    deleteEntity() {
        throw new Error('Not implemented!');
    }
    unselectEntity() {
        throw new Error('Not implemented!');
    }
    updateEntity() {
        throw new Error('Not implemented!');
    }
    getEntityId(entity) {
        throw new Error('not implemented');
    }
    makeBlur() {
        const elements = document.getElementsByClassName('sort-active');
        Array.from(elements).forEach(element => {
            element.blur();
        });
    }
    controlFlickering() {
        const elements = document.getElementsByClassName('d-block');
        Array.from(elements).forEach(element => {
            element.style.transition = 'none';
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudComponent, deps: [{ token: i1.Observable }, { token: i1.Observable }, { token: i1$1.SiActionDialogService }, { token: i3.Router }, { token: i3.ActivatedRoute }, { token: i1.Observable }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiAbstractCrudComponent, selector: "ng-component", host: { listeners: { "window:beforeunload": "beforeUnloadHandler($event)" } }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudComponent, decorators: [{
            type: Component,
            args: [{ template: '' }]
        }], ctorParameters: function () { return [{ type: i1.Observable }, { type: i1.Observable }, { type: i1$1.SiActionDialogService }, { type: i3.Router }, { type: i3.ActivatedRoute }, { type: i1.Observable }]; }, propDecorators: { beforeUnloadHandler: [{
                type: HostListener,
                args: ['window:beforeunload', ['$event']]
            }] } });

class SiAbstractCrudModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudModule, declarations: [SiAbstractCrudComponent], imports: [CommonModule], exports: [SiAbstractCrudComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAbstractCrudModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiAbstractCrudComponent],
                    imports: [CommonModule],
                    exports: [SiAbstractCrudComponent]
                }]
        }] });

class SiAuthGatewayStoreService {
    constructor() {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.SELECTED_CUSTOMER_KEY = 'selectedCustomer';
        this.selectedCustomerSub = new BehaviorSubject(undefined);
        this.selectedCustomer$ = this.selectedCustomerSub.asObservable();
    }
    setSelectedCustomer(customer) {
        if (customer) {
            this.selectedCustomer = customer;
            localStorage.setItem(this.SELECTED_CUSTOMER_KEY, JSON.stringify(customer));
            this.selectedCustomerSub.next(customer);
        }
    }
    getSelectedCustomer() {
        if (!this.selectedCustomer) {
            const storedCustomer = localStorage.getItem(this.SELECTED_CUSTOMER_KEY);
            if (storedCustomer) {
                this.selectedCustomer = JSON.parse(storedCustomer);
            }
        }
        return this.selectedCustomer;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayStoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SiAuthGatewayService {
    // host parameter is for testing, never set it in application code!
    static isBehindGateway(host) {
        if (!host) {
            host = window.location.host;
        }
        return (host.includes('.siemens.') && (host.includes('.building360') || host.includes('securitymanager')))
            || host === 'localhost:4201';
    }
    // host parameter is for testing, never set it in application code!
    static isSecurityManager(host) {
        if (!host) {
            host = window.location.host;
        }
        return (host.includes('.siemens.') && host.includes('securitymanager')) || host === 'localhost:4201';
    }
    constructor(storeService) {
        this.storeService = storeService;
        this.selectedCustomer$ = storeService.selectedCustomer$;
    }
    getSelectedCustomer() {
        return this.storeService.getSelectedCustomer();
    }
    setSelectedCustomer(customer) {
        this.storeService.setSelectedCustomer(customer);
    }
    // host parameter is for testing, never set it in application code!
    getOAuth2ClientId(host) {
        if (!SiAuthGatewayService.isSecurityManager(host)) {
            return 'building360';
        }
        return 'kong';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayService, deps: [{ token: SiAuthGatewayStoreService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SiAuthGatewayStoreService }]; } });
class SiAuthGatewayServiceImpl extends SiAuthGatewayService {
    constructor(httpClient, authGatewayConfigurationService, storeService) {
        super(storeService);
        this.httpClient = httpClient;
        this.authGatewayConfigurationService = authGatewayConfigurationService;
        this.defaultHeaders = new HttpHeaders({ accept: 'application/json' });
        this.userInfo$ = this.httpClient
            .get(this.authGatewayConfigurationService.getUserInfoUrl(), {
            headers: this.defaultHeaders
        }).pipe(map(response => {
            this.userInfo = response.data;
            return this.userInfo;
        }));
        this.csrfToken$ = this.httpClient
            .get(this.authGatewayConfigurationService.getCsrfUrl()).pipe(map(response => {
            this.csrfToken = response.csrf;
            return this.csrfToken;
        }));
    }
    getUserInfo() {
        if (this.userInfo) {
            return of(this.userInfo);
        }
        return this.userInfo$;
    }
    getCsrfToken() {
        if (this.csrfToken) {
            return of(this.csrfToken);
        }
        return this.csrfToken$;
    }
    getScope() {
        if (this.userInfo) {
            return of(this.userInfo.scope);
        }
        return this.userInfo$.pipe(map((userInfo) => userInfo.scope));
    }
    logout() {
        window.location.assign(this.authGatewayConfigurationService.getLogoutUrl());
    }
}
class SiAuthGatewayServiceMock extends SiAuthGatewayService {
    /* eslint-disable */
    static { this.userInfo = {
        user_id: 'xxx-yyy-4f5f-222',
        name: 'John Doe',
        user_name: 'John',
        given_name: 'John',
        family_name: 'Doe',
        email: 'john.doe@siemens.com',
        user_attributes: {
            picture: ['data:image/jpeg;base64,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']
        },
        email_verified: true,
        previous_logon_time: 123412121,
        sub: 'sub',
        selectedCompany: {
            id: 'd1ac2d57-ac51-4bb4-913d-f165f8f6b484',
            name: 'Demo Customer 1',
            authUrl: 'http://demo-customer-1.authprovider'
        },
        companies: [
            {
                id: 'd1ac2d57-ac51-4bb4-913d-f165f8f6b484',
                name: 'Demo Customer 1',
                subdomain: 'demo-customer-1',
                authUrl: 'http://demo-customer-1.authprovider'
            },
            {
                id: '11ad2d57-ac55-4bb4-913d-f16539874783',
                name: 'Demo Customer 2',
                subdomain: 'demo-customer-2',
                authUrl: 'http://demo-customer-2.authprovider'
            },
            {
                id: 'a1ac2d57-ac51-4bb4-913d-f165f8f6b484',
                name: 'Demo Customer 3',
                subdomain: 'demo-customer-3',
                authUrl: 'http://demo-customer-3.authprovider'
            },
            {
                id: 'ffad2d57-ac55-4bb4-913d-f16539874783',
                name: 'Demo Customer 4',
                subdomain: 'demo-customer-4',
                authUrl: 'http://demo-customer-4.authprovider'
            },
            {
                id: 'abad2d57-ac55-4bb4-913d-f16539874783',
                name: 'Demo Customer 5',
                subdomain: 'demo-customer-5',
                authUrl: 'http://demo-customer-5.authprovider'
            },
            {
                id: '12342d57-ac55-4bb4-913d-f16539874783',
                name: 'Demo Customer 6',
                subdomain: 'demo-customer-6',
                authUrl: 'http://demo-customer-6.authprovider'
            }
        ],
        scope: [
            'dash.read',
            'af.read',
            'incm.read',
            'incm.write:config',
            'an.read',
            'upm.read',
            'mats.read',
            'im.read:groups',
            'dlm.read',
            'ass.read',
            'um.read:ts',
            'sss.read',
            'im.read'
        ]
    }; }
    /* eslint-enable */
    /**
     *  Returns a mock user info
     */
    getUserInfo() {
        return of(SiAuthGatewayServiceMock.userInfo);
    }
    getCsrfToken() {
        return of('csrftoken');
    }
    getScope() {
        return of([
            'dash.read',
            'af.read',
            'incm.read',
            'incm.write:config',
            'an.read',
            'upm.read',
            'mats.read',
            'im.read:groups',
            'dlm.read',
            'ass.read',
            'um.read:ts',
            'sss.read',
            'im.read'
        ]);
    }
    logout() {
        console.log('User would be logged out in production');
    }
}

class SiAuthGatewayCsrfInterceptor {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static { this.DISABLE_HEADER = 'Disable-CSRF-Interceptor'; }
    constructor(authService, platformId) {
        this.authService = authService;
        this.isBrowser = isPlatformBrowser(platformId);
    }
    intercept(request, next) {
        // skip this interceptor for all GET requests, or if you are developing locally
        if (this.skipInterceptor(request)) {
            return next.handle(request);
        }
        if (request.headers.has(SiAuthGatewayCsrfInterceptor.DISABLE_HEADER)) {
            const updatedRequest = request.clone({
                headers: request.headers.delete(SiAuthGatewayCsrfInterceptor.DISABLE_HEADER)
            });
            return next.handle(updatedRequest);
        }
        return this.authService.getCsrfToken().pipe(exhaustMap(csrfToken => {
            let headers = request.headers;
            headers = headers.append('x-csrf', csrfToken);
            const updatedRequest = request.clone({ headers });
            return next.handle(updatedRequest);
        }));
    }
    skipInterceptor(request) {
        return request.method === 'GET'
            || (this.isBrowser && !SiAuthGatewayService.isBehindGateway())
            || this.authService instanceof SiAuthGatewayServiceMock;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCsrfInterceptor, deps: [{ token: SiAuthGatewayService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCsrfInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCsrfInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SiAuthGatewayService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

const HOST_TOKEN = new InjectionToken('host');
class SiAuthGatewayConfigurationService {
    constructor(platformId, host) {
        this.host = host;
        this.isSecurityManager = false;
        this.isEnergyManager = false;
        this.appShellServerBaseUrl = 'https://ap-shell-server.building360-dev.siemens.cloud';
        this.securityManagerUumPath = '/user-management';
        this.userInfoPath = '/api/user/info';
        this.companyPath = '/company';
        this.csrfPath = '/csrf/generate';
        this.subscriptionsPath = '/api/user/subscriptions';
        this.logoutPath = '/api/user/logout';
        if (isPlatformBrowser(platformId) && !host) {
            this.host = window.location.host;
        }
        else if (host) {
            this.host = host;
        }
        else {
            this.host = '';
        }
        this.isSecurityManager = this.host.includes('securitymanager');
        this.isEnergyManager = this.host.includes('.building360-dev.');
    }
    getUserInfoUrl() {
        if (this.isSecurityManager) {
            return this.getSecurityManagerBaseUrl() + this.userInfoPath;
        }
        else if (this.isEnergyManager) {
            return this.appShellServerBaseUrl + this.userInfoPath;
        }
        return this.userInfoPath;
    }
    getCompanyUrl() {
        if (this.isSecurityManager) {
            return this.getSecurityManagerBaseUrl() + this.companyPath;
        }
        else if (this.isEnergyManager) {
            return this.appShellServerBaseUrl + this.companyPath;
        }
        return this.companyPath;
    }
    getCsrfUrl() {
        if (this.isSecurityManager) {
            return this.getSecurityManagerBaseUrl() + this.csrfPath;
        }
        else if (this.isEnergyManager) {
            return this.appShellServerBaseUrl + this.csrfPath;
        }
        return this.csrfPath;
    }
    getLogoutUrl() {
        if (this.isSecurityManager) {
            return this.getSecurityManagerBaseUrl() + this.logoutPath;
        }
        else if (this.isEnergyManager) {
            return this.appShellServerBaseUrl + this.logoutPath;
        }
        return this.logoutPath;
    }
    getSubscriptionsUrl() {
        if (this.isSecurityManager) {
            return this.getSecurityManagerBaseUrl() + this.subscriptionsPath;
        }
        else if (this.isEnergyManager) {
            return this.appShellServerBaseUrl + this.subscriptionsPath;
        }
        return this.subscriptionsPath;
    }
    getSecurityManagerBaseUrl() {
        return this.securityManagerUumPath;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayConfigurationService, deps: [{ token: PLATFORM_ID }, { token: HOST_TOKEN, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [HOST_TOKEN]
                }, {
                    type: Optional
                }] }]; } });

class SiAuthGatewayCustomerService {
    constructor(httpClient, router, translationService, injector, storeService, authService, authGatewayConfigurationService, platformId) {
        this.httpClient = httpClient;
        this.router = router;
        this.translationService = translationService;
        this.injector = injector;
        this.storeService = storeService;
        this.authService = authService;
        this.authGatewayConfigurationService = authGatewayConfigurationService;
        this.defaultHeaders = new HttpHeaders('Accept:application/json');
        this.isBehindGateway = isPlatformBrowser(platformId) && SiAuthGatewayService.isBehindGateway();
    }
    saveToSession(company) {
        const headers = this.defaultHeaders.append(SiAuthGatewayCsrfInterceptor.DISABLE_HEADER, 'true');
        return this.httpClient
            .post(this.authGatewayConfigurationService.getCompanyUrl(), company, {
            headers,
            withCredentials: true
        })
            .toPromise();
    }
    getFromSession() {
        return this.authService
            .getUserInfo()
            .pipe(map(userInfo => userInfo.selectedCompany))
            .toPromise();
    }
    async getDefaultCustomer() {
        const companies = await this.authService
            .getUserInfo()
            .pipe(map(userInfo => userInfo.companies))
            .toPromise();
        return companies[0];
    }
    async getUserInfo() {
        return await this.authService
            .getUserInfo()
            .pipe(map(userdetails => userdetails))
            .toPromise();
    }
    setSelectedCustomer(customer) {
        this.previousCustomer = this.selectedCustomer;
        this.selectedCustomer = customer;
    }
    async selectCustomer(customer) {
        try {
            if (this.selectedCustomer?.id === customer.id) {
                return true;
            }
            if (this.authService instanceof SiAuthGatewayServiceImpl) {
                await this.saveToSession(customer);
            }
            this.storeService.setSelectedCustomer(customer);
            this.setSelectedCustomer(customer);
            if (this.authService instanceof SiAuthGatewayServiceImpl && this.previousCustomer?.authUrl) {
                window.location.replace(`${this.previousCustomer.authUrl}/logout.do?client_id=${this.authService.getOAuth2ClientId()}&user_name=${encodeURIComponent((await this.authService.getUserInfo().toPromise()).user_name)}&redirect=${encodeURIComponent(window.location.protocol + '//' + window.location.host + window.location.pathname)}`);
                // wait when browser will redirect user to auth url,
                // otherwise initializer in shared library will call SS and break the whole flow
                await new Promise(res => {
                    setTimeout(res, 10000);
                });
            }
            return true;
        }
        catch (e) {
            console.error(e);
            this.injector
                .get(SiToastNotificationService)
                .queueToastNotification('warning', this.translationService.instant('SISUITE.AUTH_GATEWAY.CUSTOMER_SELECTION_ERROR_TITLE'), this.translationService.instant('SISUITE.AUTH_GATEWAY.CUSTOMER_SELECTION_ERROR_MESSAGE'));
            return false;
        }
    }
    async selectStoredCustomer() {
        if (!this.isBehindGateway) {
            return false;
        }
        let storedCustomer;
        try {
            const customer = await this.getFromSession();
            this.setSelectedCustomer(customer);
            if (customer.id === 'sisuite') {
                storedCustomer = this.storeService.getSelectedCustomer() || (await this.getDefaultCustomer());
            }
            else {
                storedCustomer = this.storeService.getSelectedCustomer() || customer || (await this.getDefaultCustomer());
            }
            if (!storedCustomer) {
                return this.openUnauthorizedPage();
            }
            const userInfo = await this.getUserInfo();
            if (userInfo) {
                if (userInfo.companies.filter(s => s.id === storedCustomer?.id).length === 0) {
                    storedCustomer = userInfo.companies[0];
                }
            }
            if (storedCustomer) {
                return this.selectCustomer(storedCustomer);
            }
            else {
                return this.openUnauthorizedPage();
            }
        }
        catch (exception) {
            return this.openUnauthorizedPage();
        }
    }
    openUnauthorizedPage() {
        this.router.navigate(['/unauthorized']);
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCustomerService, deps: [{ token: i1$2.HttpClient }, { token: i3.Router }, { token: i3$1.TranslateService }, { token: i0.Injector }, { token: SiAuthGatewayStoreService }, { token: SiAuthGatewayService }, { token: SiAuthGatewayConfigurationService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCustomerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthGatewayCustomerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: i3.Router }, { type: i3$1.TranslateService }, { type: i0.Injector }, { type: SiAuthGatewayStoreService }, { type: SiAuthGatewayService }, { type: SiAuthGatewayConfigurationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class SiEntitySelectionDialogComponent {
    constructor() {
        this.modalTitle = '';
        this.modalSave = '';
        this.modalCancel = '';
        this.selected = [];
        this.numberOfSelections = 1;
        this.closeModal = new EventEmitter();
        this.loadedSelected = [];
        this.unloadedSelected = new Map();
        this.datatableSelectionType = SelectionType.single;
        this.page = { pageNumber: 0, totalElements: 0, size: 0, totalPages: 0 };
        this.searchString = '';
        this.tableConfig = SI_DATATABLE_CONFIG;
    }
    ngOnInit() {
        this.initUnloadedSelected();
        this.tableColumns = [{
                cellTemplate: this.titleTemplate,
                prop: 'title',
                checkboxable: this.selectionType === SelectionType.multi
            }];
        if (this.selectionType === SelectionType.single) {
            this.numberOfSelections = 1;
        }
        this.pagedData?.subscribe(pagedData => {
            this.datatableRows = pagedData.data;
            let nextDatatableSelected = [];
            if (this.selected && this.selected.length > 0) {
                nextDatatableSelected = pagedData.data.filter(selectable => {
                    if (!selectable) {
                        return false;
                    }
                    else {
                        const result = this.selected.some(selected => selected.id === selectable.id);
                        if (result) {
                            this.unloadedSelected.delete(selectable.id);
                        }
                        return result;
                    }
                });
            }
            this.loadedSelected = nextDatatableSelected;
            this.page = pagedData.page || { pageNumber: 0, totalElements: 0, size: 0, totalPages: 0 };
        });
        if (this.selectionType === SelectionType.multi) {
            this.datatableSelectionType = SelectionType.multiClick;
        }
    }
    onSelect(itemsSelectedInTheTable) {
        this.loadedSelected = [...itemsSelectedInTheTable];
        this.selected = [...this.loadedSelected, ...this.unloadedSelected.values()];
    }
    get saveDisabled() {
        if (this.numberOfSelections === undefined || this.numberOfSelections < 1) {
            return false;
        }
        else {
            return !this.selected || this.selected.length < this.numberOfSelections;
        }
    }
    saveModal() {
        if (this.selected) {
            this.closeModal.emit(this.selected);
        }
    }
    closeModalDialog() {
        this.closeModal.emit(undefined);
    }
    resetTableScroll() {
        if (this.table) {
            this.table.bodyComponent.offsetY = 0;
            this.table.element.getElementsByTagName('datatable-body')[0].scrollTop = 0;
        }
    }
    searchText(search) {
        const tmpSearch = search?.trim() ?? '';
        if (this.searchString !== tmpSearch) {
            this.initUnloadedSelected();
            this.resetTableScroll();
            this.searchString = tmpSearch;
            this.table?.recalculate();
            const requestPageSize = this.table?.pageSize ?? 50;
            this.pageRequests?.next({ pageNumber: 0, pageSize: requestPageSize, search: this.searchString });
        }
    }
    setPage(page) {
        if (!this.pageRequests) {
            return;
        }
        // We already have all data
        if (this.page && this.page.totalElements === this.page.size) {
            return;
        }
        if (this.page.pageNumber !== page.offset) {
            this.pageRequests?.next({ pageNumber: page.offset, pageSize: page.pageSize, search: this.searchString });
        }
    }
    initUnloadedSelected() {
        this.selected?.forEach(s => this.unloadedSelected.set(s.id, s));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiEntitySelectionDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiEntitySelectionDialogComponent, selector: "si-entity-selection-dialog", inputs: { modalTitle: "modalTitle", modalSave: "modalSave", modalCancel: "modalCancel", pagedData: "pagedData", pageRequests: "pageRequests", selected: "selected", selectionType: "selectionType", numberOfSelections: "numberOfSelections" }, outputs: { closeModal: "closeModal" }, viewQueries: [{ propertyName: "table", first: true, predicate: ["table"], descendants: true, static: true }, { propertyName: "titleTemplate", first: true, predicate: ["titleTemplate"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"modal-container w-100\">\n  <div class=\"modal-header\">\n    <h4 class=\"modal-title si-title-2\">{{modalTitle | translate}}</h4>\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost element-cancel\" aria-label=\"Close\" (click)=\"closeModalDialog()\">\n    </button>\n  </div>\n  <div class=\"modal-body\">\n    <si-search-bar\n      [value]=\"searchString\"\n      placeholder=\"{{ 'SISUITE.LIST-SELECT-MODAL.SEARCH' | translate }}\"\n      [colorVariant]=\"'base-0'\"\n      [showIcon]=\"true\"\n      [debounceTime]=\"400\"\n      (searchChange)=\"searchText($event)\"\n    ></si-search-bar>\n    <ngx-datatable\n      #table\n      class=\"list-table table-element elevation-0 mt-6 rounded-0\"\n      columnMode=\"force\"\n      siDatatableInteraction\n      [columns]=\"tableColumns\"\n      [count]=\"page.totalElements\"\n      [cssClasses]=\"tableConfig.cssClasses\"\n      [externalPaging]=\"true\"\n      [headerHeight]=\"0\"\n      [reorderable]=\"false\"\n      [rows]=\"datatableRows\"\n      [rowHeight]=\"tableConfig.rowHeightSmall\"\n      [scrollbarV]=\"true\"\n      [selected]=\"loadedSelected\"\n      [selectionType]=\"datatableSelectionType\"\n      (select)=\"onSelect($event.selected)\"\n      (page)=\"setPage($event)\">\n    </ngx-datatable>\n  </div>\n  <div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-secondary\" (click)=\"closeModalDialog()\">{{modalCancel | translate}}</button>\n    <button type=\"button\" class=\"btn btn-primary\" [disabled]=\"saveDisabled\" (click)=\"saveModal()\">{{modalSave | translate}}</button>\n  </div>\n</div>\n<ng-template #titleTemplate let-value=\"value\" let-row=\"row\" ngx-datatable-cell-template>\n  <span *ngIf=\"row\">\n    <span><strong>{{ row.title }}</strong></span>\n  </span>\n</ng-template>\n", styles: ["::ng-deep .datatable-header-cell label.datatable-checkbox,::ng-deep .datatable-body-cell label.datatable-checkbox{display:inline-flex;width:13px;height:13px;margin:0 16px 0 0;position:relative;top:2px}::ng-deep .datatable-header-cell label.datatable-checkbox>input,::ng-deep .datatable-body-cell label.datatable-checkbox>input{width:100%;height:100%}.list-table{height:300px;cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.DatatableComponent, selector: "ngx-datatable", inputs: ["targetMarkerTemplate", "rows", "groupRowsBy", "groupedRows", "columns", "selected", "scrollbarV", "scrollbarH", "rowHeight", "columnMode", "headerHeight", "footerHeight", "externalPaging", "externalSorting", "limit", "count", "offset", "loadingIndicator", "ghostLoadingIndicator", "selectionType", "reorderable", "swapColumns", "sortType", "sorts", "cssClasses", "messages", "rowClass", "selectCheck", "displayCheck", "groupExpansionDefault", "trackByProp", "selectAllRowsOnPage", "virtualization", "treeFromRelation", "treeToRelation", "summaryRow", "summaryHeight", "summaryPosition", "rowIdentity"], outputs: ["scroll", "activate", "select", "sort", "page", "reorder", "resize", "tableContextmenu", "treeAction"] }, { kind: "directive", type: i2$1.DataTableColumnCellDirective, selector: "[ngx-datatable-cell-template]" }, { kind: "directive", type: i1$1.SiDatatableInteractionDirective, selector: "[siDatatableInteraction]", inputs: ["selectionType", "datatableInteractionAutoSelect"], exportAs: ["si-datatable-interaction"] }, { kind: "component", type: i1$1.SiSearchBarComponent, selector: "si-search-bar", inputs: ["debounceTime", "prohibitedCharacters", "placeholder", "showIcon", "tabbable", "value", "disabled", "readonly", "colorVariant"], outputs: ["searchChange"] }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiEntitySelectionDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-entity-selection-dialog', template: "<div class=\"modal-container w-100\">\n  <div class=\"modal-header\">\n    <h4 class=\"modal-title si-title-2\">{{modalTitle | translate}}</h4>\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost element-cancel\" aria-label=\"Close\" (click)=\"closeModalDialog()\">\n    </button>\n  </div>\n  <div class=\"modal-body\">\n    <si-search-bar\n      [value]=\"searchString\"\n      placeholder=\"{{ 'SISUITE.LIST-SELECT-MODAL.SEARCH' | translate }}\"\n      [colorVariant]=\"'base-0'\"\n      [showIcon]=\"true\"\n      [debounceTime]=\"400\"\n      (searchChange)=\"searchText($event)\"\n    ></si-search-bar>\n    <ngx-datatable\n      #table\n      class=\"list-table table-element elevation-0 mt-6 rounded-0\"\n      columnMode=\"force\"\n      siDatatableInteraction\n      [columns]=\"tableColumns\"\n      [count]=\"page.totalElements\"\n      [cssClasses]=\"tableConfig.cssClasses\"\n      [externalPaging]=\"true\"\n      [headerHeight]=\"0\"\n      [reorderable]=\"false\"\n      [rows]=\"datatableRows\"\n      [rowHeight]=\"tableConfig.rowHeightSmall\"\n      [scrollbarV]=\"true\"\n      [selected]=\"loadedSelected\"\n      [selectionType]=\"datatableSelectionType\"\n      (select)=\"onSelect($event.selected)\"\n      (page)=\"setPage($event)\">\n    </ngx-datatable>\n  </div>\n  <div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-secondary\" (click)=\"closeModalDialog()\">{{modalCancel | translate}}</button>\n    <button type=\"button\" class=\"btn btn-primary\" [disabled]=\"saveDisabled\" (click)=\"saveModal()\">{{modalSave | translate}}</button>\n  </div>\n</div>\n<ng-template #titleTemplate let-value=\"value\" let-row=\"row\" ngx-datatable-cell-template>\n  <span *ngIf=\"row\">\n    <span><strong>{{ row.title }}</strong></span>\n  </span>\n</ng-template>\n", styles: ["::ng-deep .datatable-header-cell label.datatable-checkbox,::ng-deep .datatable-body-cell label.datatable-checkbox{display:inline-flex;width:13px;height:13px;margin:0 16px 0 0;position:relative;top:2px}::ng-deep .datatable-header-cell label.datatable-checkbox>input,::ng-deep .datatable-body-cell label.datatable-checkbox>input{width:100%;height:100%}.list-table{height:300px;cursor:pointer}\n"] }]
        }], propDecorators: { table: [{
                type: ViewChild,
                args: ['table', { static: true }]
            }], titleTemplate: [{
                type: ViewChild,
                args: ['titleTemplate', { static: true }]
            }], modalTitle: [{
                type: Input
            }], modalSave: [{
                type: Input
            }], modalCancel: [{
                type: Input
            }], pagedData: [{
                type: Input
            }], pageRequests: [{
                type: Input
            }], selected: [{
                type: Input
            }], selectionType: [{
                type: Input
            }], numberOfSelections: [{
                type: Input
            }], closeModal: [{
                type: Output
            }] } });

class SiModalService {
    constructor(modalService, injector) {
        this.modalService = modalService;
        this.injector = injector;
    }
    showSelectEntityDialog(modalTitle, pagedData, pageRequests, selected, modalSave = 'SISUITE.COMMON.SAVE', modalCancel = 'SISUITE.COMMON.CANCEL') {
        const selectedElements = selected ? [selected] : [];
        const config = {
            modalTitle,
            pagedData,
            pageRequests,
            selected: selectedElements,
            modalSave,
            modalCancel,
            selectionType: SelectionType.single
        };
        return this.doShowSelectEntitiesDialog(config)
            .pipe(map((result) => {
            if (result && result.length > 0) {
                return result[0];
            }
            else {
                return undefined;
            }
        }));
    }
    showSelectEntitiesDialog(modalTitle, pagedData, pageRequests, selected, modalSave = 'SISUITE.COMMON.SAVE', modalCancel = 'SISUITE.COMMON.CANCEL', numberOfSelections = 1) {
        return this.doShowSelectEntitiesDialog({
            modalTitle,
            pagedData,
            pageRequests,
            selected,
            modalSave,
            modalCancel,
            selectionType: SelectionType.multi,
            numberOfSelections
        });
    }
    doShowSelectEntitiesDialog(initialState) {
        const config = {
            initialState,
            ignoreBackdropClick: true,
            keyboard: false,
            animated: false,
            class: 'modal-dialog-centered',
            injector: this.injector
        };
        const modalRef = this.modalService.show(SiEntitySelectionDialogComponent, config);
        const modalResult = new Subject();
        modalRef.content.closeModal.pipe(first())
            .subscribe((listModalResult) => {
            modalRef.hide();
            modalResult.next(listModalResult);
            modalResult.complete();
        });
        return modalResult;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService, deps: [{ token: i1$1.SiModalService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.SiModalService }, { type: i0.Injector }]; } });

class SiModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, declarations: [SiEntitySelectionDialogComponent], imports: [CommonModule,
            NgxDatatableModule,
            SiDatatableModule,
            SiModalModule$1,
            SiSearchBarModule,
            TranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, providers: [SiModalService], imports: [CommonModule,
            NgxDatatableModule,
            SiDatatableModule,
            SiModalModule$1,
            SiSearchBarModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiEntitySelectionDialogComponent],
                    imports: [
                        CommonModule,
                        NgxDatatableModule,
                        SiDatatableModule,
                        SiModalModule$1,
                        SiSearchBarModule,
                        TranslateModule
                    ],
                    exports: [],
                    providers: [SiModalService]
                }]
        }] });

class SiTenantService {
    constructor(platformId, injector, modalService) {
        this.injector = injector;
        this.modalService = modalService;
        this.isBehindGateway = isPlatformBrowser(platformId) && SiAuthGatewayService.isBehindGateway();
        if (this.isBehindGateway) {
            this.authGatewayService = this.injector.get(SiAuthGatewayService);
            this.customerService = this.injector.get(SiAuthGatewayCustomerService);
            this.tenants$ = this.authGatewayService.getUserInfo().pipe(map(result => result.companies));
            this.selectedTenant$ = this.authGatewayService.selectedCustomer$;
        }
        else {
            this.siOAuthService = this.injector.get(SiOAuthService);
            this.tenants$ = this.siOAuthService.tenants$;
            this.selectedTenant$ = this.siOAuthService.selectedTenant$;
        }
        this.tenantSwitchMenuItem$ = combineLatest([this.tenants$, this.selectedTenant$]).pipe(map(([tenants, selectedTenant]) => this.getTenantSwitchMenuItem(tenants, selectedTenant)));
    }
    showTenantSelectionDialog() {
        let allTenants;
        if (this.isBehindGateway) {
            this.authGatewayService.getUserInfo().subscribe(value => {
                allTenants = value.companies.map((tenant) => ({
                    id: tenant.id,
                    title: tenant.name
                }));
                const selectedTenant = value.selectedCompany;
                this.showDialog(allTenants, selectedTenant);
            });
        }
        else {
            allTenants = this.siOAuthService.getTenants().map((tenant) => ({
                id: tenant.id,
                title: tenant.name
            }));
            const selectedTenant = this.siOAuthService.getCurrentTenant();
            this.showDialog(allTenants, selectedTenant);
        }
    }
    showDialog(allTenants, selectedTenant) {
        const pagedData = {
            data: allTenants,
            page: { pageNumber: 0, size: allTenants.length, totalElements: allTenants.length, totalPages: 1 }
        };
        const pageRequests = new BehaviorSubject(undefined);
        const tenants$ = combineLatest([of(pagedData), pageRequests]).pipe(map(([tenants, pr]) => {
            let filtered;
            if (pr?.search) {
                filtered = tenants.data.filter(item => item.title.toLowerCase().includes(pr.search.toLowerCase()));
            }
            else {
                filtered = tenants.data;
            }
            return {
                data: filtered,
                page: { pageNumber: 0, totalElements: filtered.length, size: filtered.length, totalPages: 1 }
            };
        }));
        this.modalService
            .showSelectEntityDialog('SISUITE.TENANT_SWITCH.CHANGE_TENANT_DIALOG_TITLE', tenants$, pageRequests, selectedTenant ? { title: selectedTenant.name, id: selectedTenant.id } : undefined, 'SISUITE.TENANT_SWITCH.CHANGE_TENANT_DIALOG_SELECT', 'SISUITE.TENANT_SWITCH.CHANGE_TENANT_DIALOG_CANCEL').subscribe(modalDialogResult => {
            if (modalDialogResult) {
                this.selectTenant(modalDialogResult.id);
            }
        });
    }
    selectTenant(tenantId) {
        if (!this.isBehindGateway) {
            this.siOAuthService.switchToTenant(tenantId);
        }
        else {
            this.authGatewayService.getUserInfo().subscribe(value => {
                for (const customer of value.companies) {
                    if (customer.id === tenantId) {
                        this.customerService.selectCustomer(customer);
                    }
                }
            });
        }
    }
    getTenantSwitchMenuItem(allTenants, selectedTenant) {
        // Case one, the user has only one or fewer tenants -> no menu item needed
        if (!allTenants || allTenants.length <= 1) {
            return undefined;
        }
        else if (allTenants.length > 1 && allTenants.length <= 10) {
            // Case two, small amount of tenants -> show in dropdown
            const tenantMenuItems = allTenants.map(tenant => ({
                title: tenant.name,
                id: tenant.id,
                action: () => this.selectTenant(tenant.id),
                selectionState: selectedTenant?.id === tenant.id ? 'check' : ''
            }));
            return {
                title: 'SISUITE.TENANT_SWITCH.TENANTS_MENU_ITEM',
                icon: 'element-shuffle',
                items: tenantMenuItems
            };
        }
        else {
            // Case three, many tenants -> show in modal selection dialog with search
            const resultMenuItem = {
                title: 'SISUITE.TENANT_SWITCH.TENANTS_MENU_ITEM',
                icon: 'element-shuffle',
                items: []
            };
            if (selectedTenant) {
                resultMenuItem.items?.push({ title: selectedTenant.name, selectionState: 'check' });
            }
            resultMenuItem.items?.push({
                title: 'SISUITE.TENANT_SWITCH.CHANGE_TENANT',
                action: () => this.showTenantSelectionDialog()
            });
            return resultMenuItem;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTenantService, deps: [{ token: PLATFORM_ID }, { token: i0.Injector }, { token: SiModalService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTenantService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTenantService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.Injector }, { type: SiModalService }]; } });

class SiSecNavService {
    constructor(httpClient, configProvider, siAuthGatewayService, platformId) {
        this.httpClient = httpClient;
        this.configProvider = configProvider;
        this.siAuthGatewayService = siAuthGatewayService;
        this.platformId = platformId;
        this.domain = 'siveillance-dev.siemens.cloud';
        this._availableMenuItems$ = new BehaviorSubject([]);
        this._currentContext$ = new BehaviorSubject('');
        this._scopes$ = new BehaviorSubject([]);
        this._showCompleteSecurityManagerNav$ = new BehaviorSubject(false);
        this.navigationItems$ = new BehaviorSubject([]);
    }
    getInitialUserInfo() {
        this.siAuthGatewayService.getUserInfo()
            .pipe(map(u => u.scope)).subscribe(scopes => {
            this._scopes$.next(scopes);
        });
        combineLatest([
            this._availableMenuItems$,
            this._currentContext$,
            this._scopes$,
            this._showCompleteSecurityManagerNav$
        ]).subscribe(([menuItems, currentContext, scopes, showCompleteNavigation]) => {
            this.updateMenuItems(menuItems, currentContext, scopes, showCompleteNavigation);
        });
    }
    loadMenuEntries() {
        this.getInitialUserInfo();
        const result = new ReplaySubject(1);
        this.fetchMenuEntries().subscribe(response => {
            this._availableMenuItems$.next(response);
            result.next(true);
            result.complete();
        }, error => {
            console.error('Cannot fetch left menu entries', error);
            result.next(false);
            result.complete();
        });
        return result;
    }
    groupIsNotEmpty(item, scopes, showCompleteNavigation) {
        return item.links.filter(link => this.hasAccess(link, scopes, showCompleteNavigation)).length > 0;
    }
    createMenuLink(item, domain, currentContext) {
        const menuItem = { title: item.title, icon: item.icon };
        if (item.navigationExtras !== undefined) {
            menuItem.navigationExtras = item.navigationExtras;
        }
        if (currentContext === item.context) {
            menuItem.link = item.link;
        }
        else {
            menuItem.href = `http://${this.getHref(domain, item)}`;
        }
        return menuItem;
    }
    isScopePredicate(scope) {
        return scope.type !== undefined;
    }
    isLink(item) {
        return item.scope !== undefined;
    }
    isGroup(item) {
        return item.links !== undefined;
    }
    fetchMenuEntries() {
        const securityManagerNavigationUrl = this.configProvider.configuration.securityManagerNavigationUrl;
        if (!securityManagerNavigationUrl) {
            throw new Error('please provide a valid securityManagerNavigationUrl');
        }
        return this.httpClient.get(`${securityManagerNavigationUrl}`)
            .pipe(map(response => response?.entries ?? []));
    }
    hasAccess(item, scopes, showCompleteNavigation) {
        if (showCompleteNavigation) {
            return true;
        }
        if (this.isScopePredicate(item.scope)) {
            if (item.scope.type === 'any') {
                return item.scope.scopes.some(s => scopes.includes(s));
            }
            else if (item.scope.type === 'all') {
                return item.scope.scopes.every(s => scopes.includes(s));
            }
            return false;
        }
        else {
            return scopes.includes(item.scope);
        }
    }
    getHref(domain, item) {
        if (SiAuthGatewayService.isSecurityManager()) {
            if (item.includeTenantId && isPlatformBrowser(this.platformId)) {
                const tenantID = this.siAuthGatewayService.getSelectedCustomer().id;
                return `${domain}/${item.context}/${tenantID}/${item.href}`;
            }
            else {
                return `${domain}/${item.context}${item.href}`;
            }
        }
        else {
            return `${item.context}.${domain}${item.href}`;
        }
    }
    setShowCompleteSecurityManagerNav(showCompleteSecurityManagerNav) {
        this._showCompleteSecurityManagerNav$.next(showCompleteSecurityManagerNav);
    }
    setSecurityManagerContext(securityManagerContext) {
        this._currentContext$.next(securityManagerContext);
    }
    setSecurityManagerScopes(scopes) {
        this._scopes$.next(scopes);
    }
    updateMenuItems(menuItems, currentContext, scopes, showCompleteNavigation) {
        const theDomain = this.domain ? this.domain : '';
        const preparedMenuItems = menuItems
            .filter(item => (this.isLink(item) && this.hasAccess(item, scopes, showCompleteNavigation)) ||
            (this.isGroup(item) && this.groupIsNotEmpty(item, scopes, showCompleteNavigation))).map(item => {
            if (this.isLink(item)) {
                return this.createMenuLink(item, theDomain, currentContext);
            }
            else {
                const children = item.links
                    .filter(child => this.isLink(child) && this.hasAccess(child, scopes, showCompleteNavigation))
                    .map(link => this.createMenuLink(link, theDomain, currentContext));
                return {
                    title: item.title,
                    icon: item.icon,
                    items: children,
                    id: item.title
                };
            }
        });
        this.navigationItems$.next(preparedMenuItems);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecNavService, deps: [{ token: i1$2.HttpClient }, { token: SiConfigProvider }, { token: SiAuthGatewayService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecNavService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecNavService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: SiConfigProvider }, { type: SiAuthGatewayService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

const LANGUAGE_TRANSLATION_PREFIX = 'SISUITE.LANGUAGE';
const knownLanguagesMap = new Map([
    ['en', 'English'],
    ['cs', 'Česky'],
    ['da', 'Dansk'],
    ['de', 'Deutsch'],
    ['es', 'Español'],
    ['fr', 'Français'],
    ['it', 'Italiano'],
    ['hu', 'Magyar'],
    ['nl', 'Nederlands'],
    ['nb-NO', 'Norsk'],
    ['pl', 'Polski'],
    ['pt', 'Português'],
    ['ro', 'Română'],
    ['sl', 'Slovenščina'],
    ['sk', 'Slovensky'],
    ['fi', 'Suomi'],
    ['sv', 'Svenska'],
    ['tr', 'Türkçe'],
    ['el', 'Ελληνικά'],
    ['ko', '한국어'],
    ['zh-Hans', '简体中文'],
    ['zh-Hant', '繁體中文'],
    ['ru', 'Русский'] // Russian
]);
class SiLanguageSwitchService {
    constructor(siLocaleService, platformId) {
        this.siLocaleService = siLocaleService;
        this.isBrowser = isPlatformBrowser(platformId);
    }
    /**
     * Loads the current configured locale of the user from the remote Siveillance cloud service
     * and updates the SiLocaleService if needed.
     * @param siLocaleService The SiLocaleService from the @simpl/element-ng library.
     */
    updateLocale() {
        if (!this.siLocaleService.locale ||
            this.siLocaleService.locale === 'template') {
            this.siLocaleService.locale = this.defaultLocale();
        }
    }
    /**
     * Returns a language switch menu item with the label `SISUITE.LANGUAGE.LANGUAGE`. We expect you
     * provide a translation into `Language` with your i18n files. The menu item has for each
     * available language a sub-menu item that is used for selection. Each sub-menu item has
     * the label `SISUITE.LANGUAGE.<LOCALE>`. The translation needs to be provided by the app.
     *
     * You can also reuse the provided i18n translation files from the assets/i18n folder.
     *
     * @param siLocaleService  The SiLocaleService from the @simpl/element-ng library.
     */
    getLanguageMenuItem() {
        const languageItems = [];
        this.siLocaleService.config.availableLocales?.forEach(language => {
            languageItems.push({
                title: knownLanguagesMap.get(language),
                action: () => (this.siLocaleService.locale = language),
                selectionState: this.siLocaleService.locale === language ? 'check' : ''
            });
        });
        return {
            title: `${LANGUAGE_TRANSLATION_PREFIX}.LANGUAGE`,
            icon: 'element-language',
            items: languageItems
        };
    }
    defaultLocale() {
        if (this.isBrowser) {
            if (!!window.navigator.language &&
                window.navigator.language.toLowerCase().includes('de')) {
                return 'de';
            }
            else {
                return 'en';
            }
        }
        else {
            return 'en';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLanguageSwitchService, deps: [{ token: i1$1.SiLocaleService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLanguageSwitchService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLanguageSwitchService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.SiLocaleService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class SiShellRemoteLocaleService {
    constructor(httpClient, configProvider) {
        this.httpClient = httpClient;
        this.configProvider = configProvider;
        this.settingsUrl = '/api/settings/me/';
        this.preferencesUrl = '/api/settings/me?appKey=General+Preferences';
    }
    /**
     * Loads the locale from the configured dashboard services. Returns undefined
     * if not dashboard is configured.
     */
    loadRemoteLocale() {
        if (!this.hasValidConfiguration()) {
            return of(undefined);
        }
        const url = this.configProvider.configuration.dashboardUrl + this.preferencesUrl;
        return this.httpClient.get(url).pipe(map(response => {
            if (response._embedded) {
                this.langPref = response._embedded['sishell:settings'].find(preference => preference.key === 'Language');
            }
            return this.langPref?.value;
        }));
    }
    /**
     * Uploads the given local to the dashboard service. It does nothing and returns true if no dashboard service
     * is configured.
     *
     * @param locale The locale string to be stored in the dashboard service.
     */
    updateRemoteLocale(locale) {
        if (!this.hasValidConfiguration()) {
            return of(true);
        }
        if (this.langPref) {
            return this.doUpdateRemoteLocale(locale);
        }
        else {
            return this.loadRemoteLocale().pipe(switchMap(() => this.doUpdateRemoteLocale(locale)));
        }
    }
    doUpdateRemoteLocale(locale) {
        if (this.langPref) {
            const theLangPref = {
                id: this.langPref.id,
                appId: this.langPref.appId,
                key: this.langPref.key,
                value: locale,
                createdByUserId: this.langPref.createdByUserId
            };
            const url = this.configProvider.configuration.dashboardUrl + this.settingsUrl + theLangPref.id;
            return this.httpClient.put(url, theLangPref, { observe: 'response' }).pipe(map((response) => response.status === 200));
        }
        else {
            throw new Error('The remote locale settings need to be loaded once, before being able to update the remove locale.');
        }
    }
    hasValidConfiguration() {
        return !!this.configProvider
            && !!this.configProvider.configuration.dashboardUrl
            && this.configProvider.configuration.dashboardUrl.length > 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellRemoteLocaleService, deps: [{ token: i1$2.HttpClient }, { token: SiConfigProvider, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellRemoteLocaleService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellRemoteLocaleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: SiConfigProvider, decorators: [{
                    type: Optional
                }] }]; } });

const COOKIE_NAME = 'sishelllanguagecookie';
const COOKIE_EXPIRES = 365 * 3;
class SiShellLocaleStoreService extends SiLocaleStore {
    constructor(cookieService, localeService, platformId, configProvider) {
        super();
        this.cookieService = cookieService;
        this.localeService = localeService;
        this.configProvider = configProvider;
        this.isBrowser = isPlatformBrowser(platformId);
    }
    get locale() {
        if (!this.isBrowser) {
            return undefined;
        }
        if (SiAuthGatewayService.isBehindGateway()) {
            return localStorage.getItem(COOKIE_NAME) ?? undefined;
        }
        else {
            if (this.cookieService.check(COOKIE_NAME)) {
                return this.cookieService.get(COOKIE_NAME);
            }
            else {
                return undefined;
            }
        }
    }
    saveLocale(locale) {
        if (SiAuthGatewayService.isBehindGateway()) {
            localStorage.setItem(COOKIE_NAME, locale);
        }
        else {
            this.deleteCookie(COOKIE_NAME);
            const cookieDomain = this.configProvider?.configuration.cookieDomain ?? undefined;
            this.cookieService.set(COOKIE_NAME, locale, COOKIE_EXPIRES, '/', cookieDomain);
            // Make sure that we save a cookie. When the config cookie domain does not fit
            // to the real domain url, we cannot save a cookie. Than the language will
            // be reloaded again and we end up in cycles. In the following we check
            // if the storage worked fine, and if not, we save it with the full hostname.
            const theNewCookie = this.cookieService.get(COOKIE_NAME);
            if (cookieDomain && !theNewCookie) {
                this.cookieService.set(COOKIE_NAME, locale, COOKIE_EXPIRES, '/', undefined);
            }
        }
        return this.localeService.updateRemoteLocale(locale)
            // Enable language switch also on dashboard communication errors
            .pipe(catchError(() => of(true)));
    }
    deleteCookie(cookieName) {
        if (!this.isBrowser) {
            return;
        }
        this.cookieService.delete(cookieName);
        this.cookieService.delete(cookieName, '/');
        if (!document.domain) {
            return;
        }
        let domainVar = '';
        document.domain.split('.').reverse().forEach(domainElement => {
            if (domainVar.length > 0) {
                domainVar = '.' + domainVar;
            }
            domainVar = domainElement + domainVar;
            this.cookieService.delete(cookieName, undefined, domainVar);
            this.cookieService.delete(cookieName, '/', domainVar);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellLocaleStoreService, deps: [{ token: i1$3.CookieService }, { token: SiShellRemoteLocaleService }, { token: PLATFORM_ID }, { token: SiConfigProvider, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellLocaleStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiShellLocaleStoreService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$3.CookieService }, { type: SiShellRemoteLocaleService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: SiConfigProvider, decorators: [{
                    type: Optional
                }] }]; } });

const dashboardUrlPrefix = '://dashboard';
const incidentsUrlPrefix = '://incident-management';
const activitiesUrlPrefix = '://activity-feed';
const analyticsUrlPrefix = '://analytics';
const privilegesUrlPrefix = '://privileges';
const timeSchedulesUrlPrefix = '://ma-time-schedules';
const dlmUrlPrefix = '://ma-door-management';
const identitiesUrlPrefix = '://identity-management';
const connectionUrlPrefix = '://sisuite-access-synch';
const systemSettingsUrlPrefix = '://system-settings';
const requestsUrlPrefix = '://request-management';
const uumUrlPrefix = '://user-management';
const tenatsUrlPrefix = '://tenants';
const selfServicesUrlPrefix = '://forms';
const securityManagerUrlPrefixes = [
    dashboardUrlPrefix,
    incidentsUrlPrefix,
    activitiesUrlPrefix,
    analyticsUrlPrefix,
    privilegesUrlPrefix,
    timeSchedulesUrlPrefix,
    dlmUrlPrefix,
    identitiesUrlPrefix,
    connectionUrlPrefix,
    systemSettingsUrlPrefix,
    requestsUrlPrefix,
    uumUrlPrefix,
    tenatsUrlPrefix,
    selfServicesUrlPrefix
];

/* eslint-disable max-len */
// Master app reference from https://code.siemens.com/horizon/apps/apps-infra/app-infra-settings/-/blob/master/modules/app-settings/app-switcher-settings.auto.tfvars.json
// Last update on 2023-03-23 09:44
/*
Master app list is at
https://code.siemens.com/horizon/apps/apps-infra/app-infra-settings/-/blob/master/modules/app-settings/app-switcher-settings.auto.tfvars.json

Step 1: Manual (for March Release)

- Use content of master list to display links to other Apps
https://code.siemens.com/horizon/apps/apps-infra/app-infra-settings/-/blob/master/modules/app-settings/app-switcher-settings.auto.tfvars.json


Horizon Apps
  - Do nothing as they already integrate the app list and filter on permissions

Security Manager
  - Show themselves
  - Accounts
  - Devices
  - Data Setup

Energy Manager
  - Show themselves
  - Accounts
  - Devices
  - Data Setup

For Energy & Security Manager, Solution Train Architects clarify with PMs for content.

Rationale: Prod list has 17 App entries, when showing all in app switcher, most of them are not accessible. So link to Account Manager should work and that one shows all accessible horizon apps

Step 2: Authorisation Decision Service (AODS)
Use static list in client and do client side filter based on result of AODS

Step 3: App Switcher Service
https://bt-clmserver01.hqs.sbt.siemens.com/ccm/web/projects/SI%20BP%20Digital%20Ecosystem%20ART%20(ccm)#action=com.ibm.team.workitem.viewWorkItem&id=1711216
*/
/* eslint-enable max-len */
const securityManagerDevDomain = 'securitymanager-dev.siemens.cloud';
const securityManagerStagingDomain = 'securitymanager-staging.siemens.cloud';
const securityManagerProdDomain = 'securitymanager.siemens.com';
/**
 * Function to return the security manager stage in the new cloud deployment.
 * This should only be used in new cloud environment and when the browser is available.
 *
 * Should be replaced by backend configuration oder time.
 */
const getSecurityManagerStage = (testHost) => {
    const host = testHost ?? window.location.host;
    if (host.includes(securityManagerProdDomain)) {
        return 'prod';
    }
    else if (host.includes(securityManagerStagingDomain)) {
        return 'staging';
    }
    else {
        return 'dev';
    }
};
const securityManagerAppId = 'app.securityManager';
const visibleAppIds = ['app.accountMgr', 'app.assetMgr', 'app.dataClassifier', securityManagerAppId];
const stageLinkMapping = {
    'dev': 'platform-int',
    'staging': 'int',
    'prod': 'prod'
};
const appLinks = {
    'app_switcher_settings': {
        'platform-int': [
            {
                'id': 'app.backOffice',
                'rank': 210,
                'title': 'Support',
                'icon': 'marengo-no-access element-working',
                'href': 'https://platform-support.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.accountMgr',
                'rank': 150,
                'title': 'Accounts',
                'icon': 'marengo-account element-account',
                'href': 'https://platform-account.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.assetMgr',
                'rank': 160,
                'title': 'Devices',
                'icon': 'marengo-panel element-canvas',
                'href': 'https://platform-assets.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.edgeAppMgr',
                'rank': 190,
                'title': 'Edge App Manager',
                'icon': 'marengo-panel element-presentation',
                'href': 'https://platform-edge.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.boic',
                'rank': 10,
                'title': 'Operations Manager',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://platform-bo.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.cerberus',
                'rank': 60,
                'title': 'Cerberus Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://platform-cerberusportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.climatixic',
                'rank': 90,
                'title': 'Climatix IC',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://www.alpha.climatixic.com/Account/ExternalLogin?provider=siemensid'
            },
            {
                'id': 'app.sinteso',
                'rank': 70,
                'title': 'Sinteso Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://platform-sintesoportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.desigofire',
                'rank': 80,
                'title': 'Desigo Fire Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://platform-desigofireportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.dashboard',
                'rank': 100,
                'title': 'Alarm Dashboard',
                'icon': 'marengo-alarm-outline element-alarm',
                'href': 'https://platform-alarm.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.reporting',
                'rank': 140,
                'title': 'Reporting',
                'icon': 'marengo-report element-report',
                'href': 'https://platform-reporting.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.ruleEditor',
                'rank': 120,
                'title': 'Rule Editor',
                'icon': 'marengo-process-value element-process-value',
                'href': 'https://platform-rule-editor.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.energyAndSust',
                'rank': 20,
                'title': 'Energy Manager',
                'icon': 'marengo-room-temperature element-energy-manager',
                'href': 'https://building360-dev.siemens.cloud'
            },
            {
                'id': 'app.360Viewer',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://dev.digitaltwin.bt.siemens.cloud'
            },
            {
                'id': 'app.buildingTwin',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://dev.digitaltwin.bt.siemens.cloud'
            },
            {
                'id': 'app.dataClassifier',
                'rank': 170,
                'title': 'Data Classifier',
                'icon': 'marengo-tag element-tag',
                'href': 'https://platform-data-classifier.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.developerportal',
                'rank': 180,
                'title': 'API Manager',
                'icon': 'element-api',
                'href': 'https://platform-api.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.opsDashboard',
                'rank': 200,
                'title': 'Support Cockpit',
                'icon': 'element-cloud-operations',
                'href': 'https://platform-operations.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.lcs',
                'rank': 400,
                'title': 'LcAS',
                'icon': 'element-smart-home-alt',
                'href': 'https://platform-lcas-ui.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.securityManager',
                'rank': 30,
                'title': 'Security Manager',
                'icon': 'element-security',
                'href': 'https://securitymanager-dev.siemens.cloud'
            }
        ],
        'prod': [
            {
                'id': 'app.backOffice',
                'rank': 210,
                'title': 'Support',
                'icon': 'marengo-no-access element-working',
                'href': 'https://support.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.accountMgr',
                'rank': 150,
                'title': 'Accounts',
                'icon': 'marengo-account element-account',
                'href': 'https://account.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.assetMgr',
                'rank': 160,
                'title': 'Devices',
                'icon': 'marengo-panel element-canvas',
                'href': 'https://assets.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.edgeAppMgr',
                'rank': 190,
                'title': 'Edge App Manager',
                'icon': 'marengo-panel element-presentation',
                'href': 'https://edge.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.boic',
                'rank': 10,
                'title': 'Operations Manager',
                'icon': 'marengo-building-automation element-project',
                'href': 'http://operationsmanager.siemens.com/#/sso'
            },
            {
                'id': 'app.cerberus',
                'rank': 60,
                'title': 'Cerberus Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://cerberusportal.siemens.com/#/sso'
            },
            {
                'id': 'app.climatixic',
                'rank': 90,
                'title': 'Climatix IC',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://www.climatixic.com/Account/ExternalLogin?provider=siemensid'
            },
            {
                'id': 'app.sinteso',
                'rank': 70,
                'title': 'Sinteso Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://sintesoportal.siemens.com/#/sso'
            },
            {
                'id': 'app.desigofire',
                'rank': 80,
                'title': 'Desigo Fire Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://desigofireportal.siemens.com/#/sso'
            },
            {
                'id': 'app.dashboard',
                'rank': 100,
                'title': 'Alarm Dashboard',
                'icon': 'marengo-alarm-outline element-alarm',
                'href': 'https://alarm.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.reporting',
                'rank': 140,
                'title': 'Reporting',
                'icon': 'marengo-report element-report',
                'href': 'https://reporting.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.ruleEditor',
                'rank': 120,
                'title': 'Rule Editor',
                'icon': 'marengo-process-value element-process-value',
                'href': 'https://rule-editor.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.energyAndSust',
                'rank': 20,
                'title': 'Energy Manager',
                'icon': 'marengo-room-temperature element-energy-manager',
                'href': 'https://app.energymanager.siemens.com'
            },
            {
                'id': 'app.360Viewer',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://buildingtwin.siemens.com'
            },
            {
                'id': 'app.buildingTwin',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://buildingtwin.siemens.com'
            },
            {
                'id': 'app.developerportal',
                'rank': 180,
                'title': 'API Manager',
                'icon': 'element-api',
                'href': 'https://api.bpcloudapps.siemens.com/#/sso'
            },
            {
                'id': 'app.securityManager',
                'rank': 30,
                'title': 'Security Manager',
                'icon': 'element-security',
                'href': 'https://securitymanager.siemens.com'
            }
        ],
        'int': [
            {
                'id': 'app.backOffice',
                'rank': 210,
                'title': 'Support',
                'icon': 'marengo-no-access element-working',
                'href': 'https://support.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.accountMgr',
                'rank': 150,
                'title': 'Accounts',
                'icon': 'marengo-account element-account',
                'href': 'https://account.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.assetMgr',
                'rank': 160,
                'title': 'Devices',
                'icon': 'marengo-panel element-canvas',
                'href': 'https://assets.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.edgeAppMgr',
                'rank': 190,
                'title': 'Edge App Manager',
                'icon': 'marengo-panel element-presentation',
                'href': 'https://edge.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.boic',
                'rank': 10,
                'title': 'Operations Manager',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://bo.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.cerberus',
                'rank': 60,
                'title': 'Cerberus Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://cerberusportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.climatixic',
                'rank': 90,
                'title': 'Climatix IC',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://www.preview.climatixic.com/Account/ExternalLogin?provider=siemensid'
            },
            {
                'id': 'app.sinteso',
                'rank': 70,
                'title': 'Sinteso Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://sintesoportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.desigofire',
                'rank': 80,
                'title': 'Desigo Fire Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://desigofireportal.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.dashboard',
                'rank': 100,
                'title': 'Alarm Dashboard',
                'icon': 'marengo-alarm-outline element-alarm',
                'href': 'https://alarm.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.reporting',
                'rank': 140,
                'title': 'Reporting',
                'icon': 'marengo-report element-report',
                'href': 'https://reporting.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.ruleEditor',
                'rank': 120,
                'title': 'Rule Editor',
                'icon': 'marengo-process-value element-process-value',
                'href': 'https://rule-editor.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.energyAndSust',
                'rank': 20,
                'title': 'Energy Manager',
                'icon': 'marengo-room-temperature element-energy-manager',
                'href': 'https://ap-shell-server.building360-test.siemens.cloud'
            },
            {
                'id': 'app.360Viewer',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://stage.digitaltwin.bt.siemens.cloud'
            },
            {
                'id': 'app.buildingTwin',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://stage.digitaltwin.bt.siemens.cloud'
            },
            {
                'id': 'app.dataClassifier',
                'rank': 170,
                'title': 'Data Setup',
                'icon': 'marengo-tag element-tag',
                'href': 'https://data-classifier.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.developerportal',
                'rank': 180,
                'title': 'API Manager',
                'icon': 'element-api',
                'href': 'https://api.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.opsDashboard',
                'rank': 200,
                'title': 'Support Cockpit',
                'icon': 'element-cloud-operations',
                'href': 'https://operations.apps.horizonint.cloud/#/sso'
            },
            {
                'id': 'app.securityManager',
                'rank': 30,
                'title': 'Security Manager',
                'icon': 'element-security',
                'href': 'https://securitymanager-staging.siemens.cloud'
            },
            {
                'id': 'app.lcs',
                'rank': 400,
                'title': 'LcAS',
                'icon': 'element-smart-home-alt',
                'href': 'https://lcas-ui.apps.horizonint.cloud/#/sso'
            }
        ],
        'dev': [
            {
                'id': 'app.backOffice',
                'rank': 210,
                'title': 'Support',
                'icon': 'marengo-no-access element-working',
                'href': 'https://support.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.accountMgr',
                'rank': 150,
                'title': 'Accounts',
                'icon': 'marengo-account element-account',
                'href': 'https://account.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.assetMgr',
                'rank': 160,
                'title': 'Devices',
                'icon': 'marengo-panel element-canvas',
                'href': 'https://assets.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.edgeAppMgr',
                'rank': 190,
                'title': 'Edge App Manager',
                'icon': 'marengo-panel element-presentation',
                'href': 'https://edge.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.boic',
                'rank': 10,
                'title': 'Operations Manager',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://bo.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.cerberus',
                'rank': 60,
                'title': 'Cerberus Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://cerberusportal.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.climatixic',
                'rank': 90,
                'title': 'Climatix IC',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://www.preview.climatixic.com/Account/ExternalLogin?provider=siemensid'
            },
            {
                'id': 'app.sinteso',
                'rank': 70,
                'title': 'Sinteso Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://sintesoportal.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.desigofire',
                'rank': 80,
                'title': 'Desigo Fire Portal',
                'icon': 'marengo-building-automation element-project',
                'href': 'https://desigofireportal.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.dashboard',
                'rank': 100,
                'title': 'Alarm Dashboard',
                'icon': 'marengo-alarm-outline element-alarm',
                'href': 'https://alarm.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.reporting',
                'rank': 140,
                'title': 'Reporting',
                'icon': 'marengo-report element-report',
                'href': 'https://reporting.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.ruleEditor',
                'rank': 120,
                'title': 'Rule Editor',
                'icon': 'marengo-process-value element-process-value',
                'href': 'https://rule-editor.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.energyAndSust',
                'rank': 20,
                'title': 'Energy Manager',
                'icon': 'marengo-room-temperature element-energy-manager',
                'href': 'https://ap-shell-server.building360-test.siemens.cloud'
            },
            {
                'id': 'app.360Viewer',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://app-360viewer.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.buildingTwin',
                'rank': 50,
                'title': '360° Viewer',
                'icon': 'marengo-room-3d element-room-3d',
                'href': 'https://app-360viewer.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.dataClassifier',
                'rank': 170,
                'title': 'Data Setup',
                'icon': 'marengo-tag element-tag',
                'href': 'https://data-classifier.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.developerportal',
                'rank': 180,
                'title': 'API Manager',
                'icon': 'element-api',
                'href': 'https://api.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.opsDashboard',
                'rank': 200,
                'title': 'Support Cockpit',
                'icon': 'element-cloud-operations',
                'href': 'https://operations.apps.horizondev.cloud/#/sso'
            },
            {
                'id': 'app.securityManager',
                'rank': 30,
                'title': 'Security Manager',
                'icon': 'element-security',
                'href': 'https://securitymanager-dev.siemens.cloud'
            },
            {
                'id': 'app.lcs',
                'rank': 400,
                'title': 'LcAS',
                'icon': 'element-smart-home-alt',
                'href': 'https://lcas-ui.apps.horizondev.cloud/#/sso'
            }
        ]
    }
};

/* eslint-disable @typescript-eslint/naming-convention */
const URL_PATH = '/api/users/me/tilegroups/Dashboard';
const ICON_MAP = {
    'siveillance-id-card': 'element-badge',
    'siveillance-activity-feed': 'element-log',
    'siveillance-time-registration': 'element-calendar',
    'siveillance-door-list': 'element-door',
    'siveillance-connected': 'element-connection-alt',
    'siveillance-key1': 'element-access-card',
    'siveillance-cardholders': 'element-account',
    'siveillance-workgroup': 'element-user-group',
    'siveillance-style-guide': 'element-plant',
    'siveillance-configuration': 'element-settings',
    'siveillance-analyze': 'element-trend'
};
class SiAppLinkService {
    constructor(httpClient, configProvider, authService, platformId) {
        this.httpClient = httpClient;
        this.configProvider = configProvider;
        this.authService = authService;
        this._groups$ = new BehaviorSubject(undefined);
        this.groups$ = this._groups$.asObservable();
        this._menuItems$ = new BehaviorSubject([]);
        this.menuItems$ = this._menuItems$.asObservable();
        this.isSecurityManager = false;
        // Indication for the new cloud setup in building-x.
        this.isBuildingX = false;
        this.isBrowser = isPlatformBrowser(platformId);
    }
    getAppLinks() {
        return this.isBuildingX ? this.getBuildingXLinks() : this.getSiSuiteLinks();
    }
    getSiSuiteLinks() {
        this.dashboardUrl = this.configProvider.configuration.dashboardUrl;
        if (this.authService.hasValidAccessToken() && this.dashboardUrl != null) {
            const tilesRequest = this.httpClient.get(this.dashboardUrl.trim() + URL_PATH);
            return tilesRequest.pipe(map(tiles => {
                const result = this.processTileGroups(tiles);
                this._groups$.next(result.groups);
                this._menuItems$.next(result.allMenuItems);
                return result.allMenuItems;
            }), catchError(() => this.getBuildingXLinks()));
        }
        else {
            return of([]);
        }
    }
    loadAppLinks() {
        const result = new ReplaySubject(1);
        this.getAppLinks().subscribe(items => {
            this._menuItems$.next(items);
            result.next(true);
            result.complete();
        }, error => {
            console.error('Cannot fetch left menu entries', error);
            result.next(false);
            result.complete();
        });
        return result;
    }
    getUrl(link) {
        if (this.isSecurityManager) {
            if (securityManagerUrlPrefixes.some(securityManagerUrl => link.indexOf(securityManagerUrl) > 0)) {
                return null;
            }
        }
        try {
            const url = new URL(link);
            if (['https:', 'http:'].includes(url.protocol)) {
                return url;
            }
            throw Error(`Unsupported link (${link})`);
        }
        catch (error) {
            // TODO: Ignore specific dashboard config entries until we get rid of the old UI
            if (!['settings', 'imprint'].includes(link)) {
                console.error(`${error} for '${link}'`);
            }
        }
        return null;
    }
    processTileGroups(appTiles) {
        const groups = [];
        const allMenuItems = [];
        if (appTiles._embedded) {
            appTiles._embedded['sishell:tilegroups'].map(tileGroups => {
                const menuItems = [];
                if (tileGroups._embedded) {
                    tileGroups._embedded['sishell:tiles'].map(tile => {
                        const link = this.getUrl(tile.link);
                        if (link) {
                            const item = this.processTile(tile);
                            allMenuItems.push(item);
                            menuItems.push(item);
                        }
                    });
                }
                if (menuItems.length > 0) {
                    groups.push({ title: tileGroups.name, tiles: menuItems });
                }
            });
        }
        return { allMenuItems, groups };
    }
    processTile(tile) {
        let theIcon = tile.titleImage.split('_')[0] !== 'siveillance-connection-manager' ?
            tile.titleImage.split('_')[0] : 'siveillance-connected';
        theIcon = ICON_MAP[theIcon] ? ICON_MAP[theIcon] : theIcon;
        if (theIcon === 'element-connection-alt' && tile.title === 'Health') {
            theIcon = 'element-diagnostic';
        }
        const theTitle = tile.title.includes('Tile_') ? `SISUITE.APP_SWITCH.${tile.title}` : tile.title;
        return {
            title: theTitle,
            icon: theIcon,
            href: tile.link
        };
    }
    getBuildingXLinks() {
        // Identify where we are
        const stage = !this.isBrowser ? 'dev' : getSecurityManagerStage();
        // Map to horizon stage
        const buildingXStage = stageLinkMapping[stage];
        // Filter apps and sort by rank
        if (this.configProvider.configuration.securityManagerNavigationUrl) {
            const url = this.configProvider.configuration.securityManagerNavigationUrl.replace('menu-entries.json', 'launchpad.json');
            return this.httpClient.get(url).pipe(map(result => this.filterAppLinks(result, buildingXStage)), catchError(error => {
                console.error('Cannot load launchpad items from service. Will use local configuration', error);
                return of(this.filterAppLinks(appLinks, buildingXStage));
            }));
        }
        return of(this.filterAppLinks(appLinks, buildingXStage));
    }
    filterAppLinks(links, buildingXStage) {
        return links.app_switcher_settings[buildingXStage]
            .filter(appLink => {
            if (appLink.id === securityManagerAppId) {
                appLink.isActive = true;
            }
            return visibleAppIds.includes(appLink.id);
        })
            .sort((a, b) => a.rank - b.rank);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppLinkService, deps: [{ token: i1$2.HttpClient }, { token: SiConfigProvider }, { token: i3$2.OAuthService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppLinkService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppLinkService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: SiConfigProvider }, { type: i3$2.OAuthService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class SiThemeRuntimeSwitchService {
    constructor(httpClient, configProvider, platformId) {
        this.httpClient = httpClient;
        this.configProvider = configProvider;
        this.isBrowser = isPlatformBrowser(platformId);
    }
    checkForSiemensBrandSwitch() {
        this.hasSiemensBrandToggleSwitch().subscribe(switchToBrand => {
            if (switchToBrand) {
                this.switchToSiemensBrand();
            }
        });
    }
    hasSiemensBrandToggleSwitch() {
        const securityManagerNavigationUrl = this.configProvider.configuration.securityManagerNavigationUrl;
        if (!securityManagerNavigationUrl) {
            return of(false);
        }
        const siemensBrandCheckUrl = securityManagerNavigationUrl.replace('menu-entries.json', `theme.json`);
        return this.httpClient.get(siemensBrandCheckUrl).pipe(map(theme => theme.themeName === 'siemens-brand'), catchError(() => of(false)));
    }
    switchToSiemensBrand() {
        if (!this.isBrowser) {
            return;
        }
        const classList = document.documentElement.classList;
        classList.forEach(c => {
            if (c.startsWith('theme-')) {
                classList.remove(c);
            }
        });
        classList.add(`theme-siemens-brand`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiThemeRuntimeSwitchService, deps: [{ token: i1$2.HttpClient }, { token: SiConfigProvider }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiThemeRuntimeSwitchService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiThemeRuntimeSwitchService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: SiConfigProvider }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class SiNotificationBarComponent {
    constructor(siOAuthService) {
        this.siOAuthService = siOAuthService;
        this.setNotificationMsg();
    }
    setNotificationMsg() {
        const selectedTenant$ = this.siOAuthService.selectedTenant$;
        this.subscription = selectedTenant$.subscribe(selectedTenant => {
            if (selectedTenant?.isDemoPeriod && this.isExpiringDateReached(selectedTenant?.endOfSubscription)) {
                this.notificationMsg = 'SISUITE.NOTIFICATION-BAR.DEMO-MODE-AND-EXPIRY-MSG';
            }
            else if (selectedTenant?.isDemoPeriod) {
                this.notificationMsg = 'SISUITE.NOTIFICATION-BAR.DEMO-MODE-MSG';
            }
            else if (this.isExpiringDateReached(selectedTenant?.endOfSubscription)) {
                this.notificationMsg = 'SISUITE.NOTIFICATION-BAR.EXPIRY-MSG';
            }
        });
    }
    isExpiringDateReached(endOfSubscription) {
        if (endOfSubscription) {
            const differenceInMs = new Date(endOfSubscription).getTime() - new Date(Date.now()).getTime();
            const oneDayMs = 1000 * 60 * 60 * 24;
            return Math.floor(differenceInMs / oneDayMs) < 7;
        }
        else {
            return false;
        }
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarComponent, deps: [{ token: i1$4.SiOAuthService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiNotificationBarComponent, selector: "si-notification-bar", inputs: { customClass: "customClass" }, ngImport: i0, template: "<div *ngIf=\"notificationMsg\" [ngClass]=\"customClass\">\n  <si-inline-notification severity=\"warning\" [message]=\"notificationMsg\">\n  </si-inline-notification>\n</div>\n", dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i1$1.SiInlineNotificationComponent, selector: "si-inline-notification", inputs: ["severity", "heading", "message", "action", "translationParams", "embedded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-notification-bar', template: "<div *ngIf=\"notificationMsg\" [ngClass]=\"customClass\">\n  <si-inline-notification severity=\"warning\" [message]=\"notificationMsg\">\n  </si-inline-notification>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1$4.SiOAuthService }]; }, propDecorators: { customClass: [{
                type: Input
            }] } });

class SiSecNavigationComponent {
    constructor() {
        this.menuItems = [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecNavigationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSecNavigationComponent, selector: "si-sec-navigation", inputs: { menuItems: "menuItems" }, ngImport: i0, template: "<si-navbar-vertical\n  stateId=\"sec-manager-vertical-nav\"\n  [items]=\"menuItems\"\n  [searchable]=\"false\">\n  <div class=\"si-layout-fixed-height\">\n    <si-notification-bar customClass=\"px-6 pt-6 px-md-9 page-top-title\"></si-notification-bar>\n    <ng-content></ng-content>\n  </div>\n</si-navbar-vertical>\n", styles: [":host{display:flex;flex:1 1 0;flex-direction:column}\n"], dependencies: [{ kind: "component", type: i1$1.SiNavbarVerticalComponent, selector: "si-navbar-vertical", inputs: ["collapsed", "searchable", "searchPlaceholder", "items", "textOnly", "visible", "autoCollapseDelay", "navbarExpandButtonText", "navbarCollapseButtonText", "stateId"], outputs: ["itemsChange", "searchEvent"] }, { kind: "component", type: SiNotificationBarComponent, selector: "si-notification-bar", inputs: ["customClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-sec-navigation', template: "<si-navbar-vertical\n  stateId=\"sec-manager-vertical-nav\"\n  [items]=\"menuItems\"\n  [searchable]=\"false\">\n  <div class=\"si-layout-fixed-height\">\n    <si-notification-bar customClass=\"px-6 pt-6 px-md-9 page-top-title\"></si-notification-bar>\n    <ng-content></ng-content>\n  </div>\n</si-navbar-vertical>\n", styles: [":host{display:flex;flex:1 1 0;flex-direction:column}\n"] }]
        }], propDecorators: { menuItems: [{
                type: Input
            }] } });

const THEME_COOKIE_NAME = 'theme';
class SiAppComponent {
    set userGuideLink(userGuideLink) {
        if (!userGuideLink) {
            userGuideLink = 'SISUITE.COMMON.USER_GUIDE_LINK';
        }
        this._userGuideLink = userGuideLink;
    }
    get userGuideLink() {
        return this._userGuideLink;
    }
    constructor(secNavService, localeService, appLinkService, themeSwitcher, cookies, platformId, injector, siemensBrandToggleService) {
        this.secNavService = secNavService;
        this.localeService = localeService;
        this.appLinkService = appLinkService;
        this.themeSwitcher = themeSwitcher;
        this.cookies = cookies;
        this.injector = injector;
        this.siemensBrandToggleService = siemensBrandToggleService;
        this._userGuideLink = 'SISUITE.COMMON.USER_GUIDE_LINK';
        this.appTitle = 'Security Manager';
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.logoutMenuItem = {
            title: 'SISUITE.COMMON.LOGOUT',
            icon: 'element-logout',
            action: () => this.logout()
        };
        this.disableAppLinkLoading = false;
        this.skipThemeCheck = false;
        this.loginCompleted = new EventEmitter();
        this.appSwitcherAppItems = [];
        this.user = { title: '' };
        this.subscriptions = [];
        this.themeAutoSwitch = {
            title: 'SISUITE.THEME.AUTO',
            action: () => this.setTheme('auto')
        };
        this.themeLightSwitch = {
            title: 'SISUITE.THEME.LIGHT',
            action: () => this.setTheme('light')
        };
        this.themeDarkSwitch = {
            title: 'SISUITE.THEME.DARK',
            action: () => this.setTheme('dark')
        };
        this.themeSwitch = {
            title: 'SISUITE.THEME.THEME',
            icon: 'element-palette',
            items: [this.themeAutoSwitch, this.themeLightSwitch, this.themeDarkSwitch]
        };
        this.isBrowser = isPlatformBrowser(platformId);
        this.isBehindGateway =
            this.isBrowser && SiAuthGatewayService.isBehindGateway();
        if (!this.isBehindGateway) {
            this.menuItemsLoaded$ = this.appLinkService.loadAppLinks();
            this.leftNavItems$ = this.appLinkService.menuItems$;
            this.siOAuthService = this.injector.get(SiOAuthService);
            this.isAuthenticated$ = this.siOAuthService.isAuthenticated$;
            this.isLoginCompleted$ = this.siOAuthService.isLoginCompleted$;
            this.tenantService = this.injector.get(SiTenantService);
        }
        else {
            this.appLinkService.isBuildingX = true;
            this.authGatewayService =
                this.injector.get(SiAuthGatewayService);
            this.tenantService = this.injector.get(SiTenantService);
            this.menuItemsLoaded$ = this.secNavService.loadMenuEntries();
            this.leftNavItems$ = this.secNavService.navigationItems$;
        }
        if (this.isBrowser) {
            if (!this.skipThemeCheck) {
                this.siemensBrandToggleService.checkForSiemensBrandSwitch();
            }
            this.languageSwitch = this.localeService.getLanguageMenuItem();
            this.accountItems = [this.languageSwitch, this.logoutMenuItem];
            if (!this.isBehindGateway) {
                this.subscriptions.push(this.siOAuthService.user$
                    .pipe(map((user) => {
                    if (user) {
                        return { title: user.name };
                    }
                    else {
                        return { title: '' };
                    }
                }))
                    .subscribe(user => {
                    this.user = user;
                    if (user.title !== '') {
                        this.localeService.updateLocale();
                    }
                }));
                this.subscriptions.push(this.tenantService.tenantSwitchMenuItem$.subscribe((tenantMenuItem) => {
                    if (tenantMenuItem) {
                        this.accountItems = [
                            tenantMenuItem,
                            this.languageSwitch,
                            this.themeSwitch,
                            { title: '-' },
                            this.logoutMenuItem
                        ];
                    }
                    else {
                        this.accountItems = [
                            this.languageSwitch,
                            this.themeSwitch,
                            { title: '-' },
                            this.logoutMenuItem
                        ];
                    }
                }));
            }
            else {
                this.localeService.updateLocale();
                this.subscriptions.push(this.authGatewayService.getUserInfo().subscribe(userInfo => {
                    if (userInfo) {
                        const accountItemClone = Object.assign({}, this.user);
                        accountItemClone.title = userInfo.name;
                        accountItemClone.email = userInfo.email;
                        if (userInfo.user_attributes?.picture != null) {
                            accountItemClone.image = userInfo.user_attributes.picture[0];
                            accountItemClone.icon = undefined;
                        }
                        this.user = accountItemClone;
                        let selectedCustomer = this.authGatewayService.getSelectedCustomer();
                        if (userInfo.companies.filter(s => s.id === selectedCustomer?.id).length === 0) {
                            selectedCustomer = undefined;
                        }
                        if (!selectedCustomer) {
                            selectedCustomer = userInfo.selectedCompany
                                ? userInfo.selectedCompany
                                : userInfo.companies[0];
                        }
                        this.authGatewayService.selectedCustomer$.subscribe(selected => (this.user.company = selected?.name));
                        this.authGatewayService.setSelectedCustomer(selectedCustomer);
                        if (!SiAuthGatewayService.isSecurityManager()) {
                            // this is for the deployments behind the SBS gateway
                            // userinfo.scope is not supported
                            this.showCompleteSecurityManagerNav = true;
                        }
                        this.subscriptions.push(this.tenantService.tenantSwitchMenuItem$.subscribe((tenantMenuItem) => {
                            if (tenantMenuItem) {
                                this.accountItems = [
                                    tenantMenuItem,
                                    this.languageSwitch,
                                    this.themeSwitch,
                                    { title: '-' },
                                    this.logoutMenuItem
                                ];
                            }
                            else {
                                this.accountItems = [
                                    this.languageSwitch,
                                    this.themeSwitch,
                                    { title: '-' },
                                    this.logoutMenuItem
                                ];
                            }
                        }));
                    }
                }, error => console.error('Cannot load information about current user.', error)));
            }
        }
    }
    ngOnInit() {
        if (this.isBrowser) {
            this.setTheme(undefined);
            if (!this.isBehindGateway) {
                try {
                    this.siOAuthService.login().then(result => {
                        this.onLoginFlowCompleted(result);
                    });
                }
                catch (err) {
                    console.error('error while handling auth flow', err);
                }
            }
            else {
                this.setupLaunchpad();
            }
        }
    }
    prependHttps(url) {
        if (!url) {
            return '';
        }
        return !url.startsWith('https://') && !url.startsWith('http://')
            ? 'https://' + url
            : url;
    }
    onLoginFlowCompleted(success) {
        if (success) {
            if (!this.disableAppLinkLoading) {
                this.secNavService.setSecurityManagerScopes(this.siOAuthService.getScopes());
                this.setupLaunchpad();
            }
        }
        this.loginCompleted.emit(success);
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
    ngOnChanges(changes) {
        if (this.securityManagerContext) {
            this.appLinkService.isSecurityManager = true;
        }
        if (changes.showCompleteSecurityManagerNav) {
            this.secNavService.setShowCompleteSecurityManagerNav(changes.showCompleteSecurityManagerNav.currentValue);
        }
        if (changes.securityManagerContext) {
            this.secNavService.setSecurityManagerContext(changes.securityManagerContext.currentValue);
        }
        if (changes.themeCookieDomain && this.themeCookieDomain) {
            this.secNavService.domain = this.themeCookieDomain;
        }
    }
    setupLaunchpad() {
        this.appLinkService
            .getAppLinks()
            .pipe(first())
            .subscribe(menuItems => (this.appSwitcherAppItems = menuItems), error => console.error('Cannot load information about other applications.', error));
    }
    logout() {
        if (!this.isBehindGateway) {
            this.siOAuthService.logout();
        }
        else {
            this.authGatewayService.logout();
        }
    }
    setTheme(theme) {
        if (!this.isBrowser) {
            return;
        }
        if (!theme) {
            // If undefined, load stored theme if available or use theme auto
            const storedTheme = this.isBehindGateway
                ? localStorage.getItem(THEME_COOKIE_NAME)
                : this.cookies.get(THEME_COOKIE_NAME);
            if (storedTheme === 'auto' ||
                storedTheme === 'light' ||
                storedTheme === 'dark') {
                theme = storedTheme;
            }
            else {
                theme = 'auto';
            }
        }
        if (theme === 'auto') {
            this.themeAutoSwitch.selectionState = 'check';
            this.themeDarkSwitch.selectionState = '';
            this.themeLightSwitch.selectionState = '';
        }
        else if (theme === 'dark') {
            this.themeAutoSwitch.selectionState = '';
            this.themeDarkSwitch.selectionState = 'check';
            this.themeLightSwitch.selectionState = '';
        }
        else {
            this.themeAutoSwitch.selectionState = '';
            this.themeDarkSwitch.selectionState = '';
            this.themeLightSwitch.selectionState = 'check';
        }
        if (this.isBehindGateway) {
            localStorage.setItem(THEME_COOKIE_NAME, theme);
        }
        else {
            this.cookies.set(THEME_COOKIE_NAME, theme, undefined, '/', this.themeCookieDomain);
        }
        this.themeSwitcher.applyThemeType(theme);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppComponent, deps: [{ token: SiSecNavService }, { token: SiLanguageSwitchService }, { token: SiAppLinkService }, { token: i1$1.SiThemeService }, { token: i1$3.CookieService }, { token: PLATFORM_ID }, { token: i0.Injector }, { token: SiThemeRuntimeSwitchService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiAppComponent, selector: "si-app", inputs: { userGuideLink: "userGuideLink", themeCookieDomain: "themeCookieDomain", appTitle: "appTitle", home: "home", logoutMenuItem: "logoutMenuItem", disableAppLinkLoading: "disableAppLinkLoading", skipThemeCheck: "skipThemeCheck", securityManagerContext: "securityManagerContext", showCompleteSecurityManagerNav: "showCompleteSecurityManagerNav" }, outputs: { loginCompleted: "loginCompleted" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"app d-flex flex-column has-navbar-fixed-top flex-grow-1\">\n  <si-navbar-primary\n    [appTitle]=\"appTitle\"\n    [appItems]=\"appSwitcherAppItems\"\n    [appSwitcherSubTitle]=\"''\"\n    [home]=\"home\"\n    [account]=\"user\"\n    [accountItems]=\"accountItems\"\n  >\n    <si-navbar-item\n      quickAction\n      [item]=\"{\n        title: 'Help',\n        icon: 'element-help',\n        items: [\n          {\n            title: 'SISUITE.COMMON.USER_GUIDE',\n            href: prependHttps(userGuideLink | translate),\n            target: '_blank'\n          },\n          {\n            title: 'SISUITE.COMMON.TRAININGS',\n            href: prependHttps('SISUITE.HELP.TRAININGS_URL' | translate),\n            target: '_blank'\n          },\n          { title: '-' },\n          { title: 'SISUITE.COMMON.ABOUT', link: 'main/about' },\n          {\n            title: 'SISUITE.COMMON.CORPORATE_INFORMATION',\n            href: prependHttps(\n              'SISUITE.HELP.CORPORATE_INFORMATION_URL' | translate\n            ),\n            target: '_blank'\n          }\n        ]\n      }\"\n    ></si-navbar-item>\n  </si-navbar-primary>\n  <si-loading-spinner\n    *ngIf=\"\n      (isAuthenticated$ | async) === false ||\n      (isLoginCompleted$ | async) === false\n    \"\n    class=\"mt-6 align-self-center\"\n  ></si-loading-spinner>\n  <si-sec-navigation\n    *ngIf=\"securityManagerContext && (menuItemsLoaded$ | async)\"\n    [menuItems]=\"leftNavItems$ | async\"\n  >\n    <router-outlet></router-outlet>\n  </si-sec-navigation>\n\n  <router-outlet *ngIf=\"!securityManagerContext\"></router-outlet>\n</div>\n", styles: [":host{display:flex;flex-direction:column;width:100%;max-width:100%;height:100%}.app{min-height:0}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.RouterOutlet, selector: "router-outlet", inputs: ["name"], outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }, { kind: "component", type: i1$1.SiLoadingSpinnerComponent, selector: "si-loading-spinner", inputs: ["isBlockingSpinner", "isSpinnerOverlay"] }, { kind: "component", type: i1$1.SiNavbarItemComponent, selector: "si-navbar-item", inputs: ["item", "quickAction"] }, { kind: "component", type: i1$1.SiNavbarPrimaryComponent, selector: "si-navbar-primary", inputs: ["primaryItems", "accountItems", "account", "logoUrl", "appTitle", "home", "appSwitcherTitle", "appSwitcherSubTitle", "favoriteAppsTitle", "defaultAppsTitle", "showMoreAppsTitle", "showLessAppsTitle", "appItems", "appCategoryItems", "appItemsFavorites", "allAppsLink", "focusOnLoad", "navAriaLabel", "closeAppSwitcherText", "openAppSwitcherText", "toggleNavigationText", "ariaLabelMainMenu", "ariaLabelSecondaryMenu"], outputs: ["appItemFavoriteChanged"] }, { kind: "component", type: SiSecNavigationComponent, selector: "si-sec-navigation", inputs: ["menuItems"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-app', template: "<div class=\"app d-flex flex-column has-navbar-fixed-top flex-grow-1\">\n  <si-navbar-primary\n    [appTitle]=\"appTitle\"\n    [appItems]=\"appSwitcherAppItems\"\n    [appSwitcherSubTitle]=\"''\"\n    [home]=\"home\"\n    [account]=\"user\"\n    [accountItems]=\"accountItems\"\n  >\n    <si-navbar-item\n      quickAction\n      [item]=\"{\n        title: 'Help',\n        icon: 'element-help',\n        items: [\n          {\n            title: 'SISUITE.COMMON.USER_GUIDE',\n            href: prependHttps(userGuideLink | translate),\n            target: '_blank'\n          },\n          {\n            title: 'SISUITE.COMMON.TRAININGS',\n            href: prependHttps('SISUITE.HELP.TRAININGS_URL' | translate),\n            target: '_blank'\n          },\n          { title: '-' },\n          { title: 'SISUITE.COMMON.ABOUT', link: 'main/about' },\n          {\n            title: 'SISUITE.COMMON.CORPORATE_INFORMATION',\n            href: prependHttps(\n              'SISUITE.HELP.CORPORATE_INFORMATION_URL' | translate\n            ),\n            target: '_blank'\n          }\n        ]\n      }\"\n    ></si-navbar-item>\n  </si-navbar-primary>\n  <si-loading-spinner\n    *ngIf=\"\n      (isAuthenticated$ | async) === false ||\n      (isLoginCompleted$ | async) === false\n    \"\n    class=\"mt-6 align-self-center\"\n  ></si-loading-spinner>\n  <si-sec-navigation\n    *ngIf=\"securityManagerContext && (menuItemsLoaded$ | async)\"\n    [menuItems]=\"leftNavItems$ | async\"\n  >\n    <router-outlet></router-outlet>\n  </si-sec-navigation>\n\n  <router-outlet *ngIf=\"!securityManagerContext\"></router-outlet>\n</div>\n", styles: [":host{display:flex;flex-direction:column;width:100%;max-width:100%;height:100%}.app{min-height:0}\n"] }]
        }], ctorParameters: function () { return [{ type: SiSecNavService }, { type: SiLanguageSwitchService }, { type: SiAppLinkService }, { type: i1$1.SiThemeService }, { type: i1$3.CookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.Injector }, { type: SiThemeRuntimeSwitchService }]; }, propDecorators: { userGuideLink: [{
                type: Input
            }], themeCookieDomain: [{
                type: Input
            }], appTitle: [{
                type: Input
            }], home: [{
                type: Input
            }], logoutMenuItem: [{
                type: Input
            }], disableAppLinkLoading: [{
                type: Input
            }], skipThemeCheck: [{
                type: Input
            }], securityManagerContext: [{
                type: Input
            }], showCompleteSecurityManagerNav: [{
                type: Input
            }], loginCompleted: [{
                type: Output
            }] } });

class SiNotificationBarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarModule, declarations: [SiNotificationBarComponent], imports: [CommonModule,
            RouterModule,
            SiInlineNotificationModule,
            TranslateModule], exports: [SiNotificationBarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarModule, imports: [CommonModule,
            RouterModule,
            SiInlineNotificationModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNotificationBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiNotificationBarComponent],
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiInlineNotificationModule,
                        TranslateModule
                    ],
                    exports: [SiNotificationBarComponent]
                }]
        }] });

const appLoadFactory = (customerService) => async () => {
    await customerService.selectStoredCustomer();
};
const buildTranslationResources = (applicationTranslationResources) => {
    const globalTranslations = [
        { prefix: '/shell/navigation/i18n/', suffix: '.json' }
    ];
    return [...globalTranslations, ...applicationTranslationResources];
};
const adaptLocale = (locale) => {
    switch (locale) {
        case 'nb-NO':
            return 'nb';
        default:
            return locale;
    }
};
class SiAppModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAppModule, declarations: [SiAppComponent, SiSecNavigationComponent], imports: [CommonModule,
            RouterModule,
            SiLoadingSpinnerModule,
            SiNavbarModule,
            SiNavbarVerticalModule,
            SiNotificationBarModule,
            TranslateModule], exports: [SiAppComponent, SiSecNavigationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppModule, providers: [
            SiTenantService,
            SiThemeService,
            {
                provide: HTTP_INTERCEPTORS,
                useClass: SiAuthGatewayCsrfInterceptor,
                multi: true,
            },
            {
                provide: APP_INITIALIZER,
                useFactory: appLoadFactory,
                multi: true,
                deps: [SiAuthGatewayCustomerService],
            },
            {
                provide: SiAuthGatewayService,
                useFactory: (httpClient, platformId, gatewayConfig, storeService) => {
                    if (isPlatformBrowser(platformId)) {
                        if (window.location.host === 'localhost:4201') {
                            return new SiAuthGatewayServiceMock(storeService);
                        }
                        else {
                            return new SiAuthGatewayServiceImpl(httpClient, gatewayConfig, storeService);
                        }
                    }
                    else {
                        return new SiAuthGatewayServiceImpl(httpClient, gatewayConfig, storeService);
                    }
                },
                multi: false,
                deps: [
                    HttpClient,
                    PLATFORM_ID,
                    SiAuthGatewayConfigurationService,
                    SiAuthGatewayStoreService,
                ],
            },
        ], imports: [CommonModule,
            RouterModule,
            SiLoadingSpinnerModule,
            SiNavbarModule,
            SiNavbarVerticalModule,
            SiNotificationBarModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAppModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiAppComponent, SiSecNavigationComponent],
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiLoadingSpinnerModule,
                        SiNavbarModule,
                        SiNavbarVerticalModule,
                        SiNotificationBarModule,
                        TranslateModule,
                    ],
                    exports: [SiAppComponent, SiSecNavigationComponent],
                    providers: [
                        SiTenantService,
                        SiThemeService,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: SiAuthGatewayCsrfInterceptor,
                            multi: true,
                        },
                        {
                            provide: APP_INITIALIZER,
                            useFactory: appLoadFactory,
                            multi: true,
                            deps: [SiAuthGatewayCustomerService],
                        },
                        {
                            provide: SiAuthGatewayService,
                            useFactory: (httpClient, platformId, gatewayConfig, storeService) => {
                                if (isPlatformBrowser(platformId)) {
                                    if (window.location.host === 'localhost:4201') {
                                        return new SiAuthGatewayServiceMock(storeService);
                                    }
                                    else {
                                        return new SiAuthGatewayServiceImpl(httpClient, gatewayConfig, storeService);
                                    }
                                }
                                else {
                                    return new SiAuthGatewayServiceImpl(httpClient, gatewayConfig, storeService);
                                }
                            },
                            multi: false,
                            deps: [
                                HttpClient,
                                PLATFORM_ID,
                                SiAuthGatewayConfigurationService,
                                SiAuthGatewayStoreService,
                            ],
                        },
                    ],
                }]
        }] });

class SiFormGroupComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * The heading of the form group
         */
        this.heading = '';
        /**
         * CSS class(es) applied to the heading of the form group. Per default,
         * the Bootstrap classes `font-weight-bold` and `mb-6` are set.
         */
        this.headingClass = 'fw-bold mb-6';
        /**
         * CSS class(es) applied to the content of the form group
         */
        this.contentClass = '';
        /**
         * resizeContainerBreakpoints allows for setting custom resize container breakpoints,
         * e.g. from within the form group container when making use of content children.
         * Per default, the constant `APP_BS_BREAKPOINTS` is used.
         */
        this.resizeContainerBreakpoints = BOOTSTRAP_BREAKPOINTS;
        this.toggleElementVisibility(false);
    }
    ngAfterViewInit() {
        // trigger a window resize event to make sure the correct container class
        // is applied to the form group
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            this.toggleElementVisibility(true);
        });
    }
    toggleElementVisibility(visible) {
        const hostElement = this.elementRef.nativeElement;
        hostElement.style.visibility = visible ? '' : 'hidden';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiFormGroupComponent, selector: "si-form-group", inputs: { heading: "heading", headingClass: "headingClass", contentClass: "contentClass", resizeContainerBreakpoints: "resizeContainerBreakpoints" }, ngImport: i0, template: "<div class=\"mb-6 d-flex flex-column flex-grow-1\" siResponsiveContainer [breakpoints]=\"resizeContainerBreakpoints\">\n  <div class=\"form-group-heading\" [ngClass]=\"headingClass\">{{ heading | translate }}</div>\n  <div class=\"form-group-content flex-grow-1\" [ngClass]=\"contentClass\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.SiResponsiveContainerDirective, selector: "[siResponsiveContainer]", inputs: ["resizeThrottle", "breakpoints"], exportAs: ["siResponsiveContainer"] }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-group', template: "<div class=\"mb-6 d-flex flex-column flex-grow-1\" siResponsiveContainer [breakpoints]=\"resizeContainerBreakpoints\">\n  <div class=\"form-group-heading\" [ngClass]=\"headingClass\">{{ heading | translate }}</div>\n  <div class=\"form-group-content flex-grow-1\" [ngClass]=\"contentClass\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { heading: [{
                type: Input
            }], headingClass: [{
                type: Input
            }], contentClass: [{
                type: Input
            }], resizeContainerBreakpoints: [{
                type: Input
            }] } });

class SiFormGroupContainerComponent {
    onResize(_) {
        this.applyAppropriateGridTemplateColumns();
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * The minimum width of each form group. Defaults to `0px`.
         */
        this.groupMinWidth = '0px';
        /**
         * The maximum width of each form group. Defaults to `1fr`.
         */
        this.groupMaxWidth = '1fr';
        /**
         * The maxmimum number of form groups per row. Defaults to `3`.
         */
        this.maxFormGroupsPerRow = 3;
        /**
         * The column gap between two sibling form groups. Defaults to `32px`.
         */
        this.columnGap = '32px';
        /**
         * The row gap between two sibling form groups. Defaults to `32px`.
         */
        this.rowGap = '32px';
        this.subscriptions = [];
        this.nativeElement = this.elementRef.nativeElement;
        this.toggleElementVisibility(false);
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.applyGridLayout();
            this.toggleElementVisibility(true);
        });
        this.subscriptions.push(this.formGroups.changes.subscribe(formGroups => {
            const previousFormGroupsCount = this.formGroupsCount;
            this.formGroupsCount = formGroups.length;
            if (previousFormGroupsCount !== this.formGroupsCount) {
                this.applyGridLayout();
            }
        }));
    }
    ngOnChanges(changes) {
        const gridLayoutChanged = changes.groupMinWidth?.currentValue ||
            changes.groupMaxWidth?.currentValue ||
            changes.columnGap?.currentValue ||
            changes.maxFormGroupsPerRow?.currentValue;
        if (gridLayoutChanged) {
            this.applyGridLayout();
        }
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
    determineGridTemplateColumns() {
        if (this.maxFormGroupsPerRow) {
            this.formGroupsCount = this.formGroups?.length || 0;
            const formGroupsPerRow = Math.min(this.maxFormGroupsPerRow, this.formGroupsCount);
            if (this.groupMaxWidth === '1fr') {
                this.templateColumns = `repeat(auto-fit,
          minmax(
            max(${this.groupMinWidth}, (100% - (${formGroupsPerRow - 1} * ${this.columnGap}))/${formGroupsPerRow}),
            ${this.groupMaxWidth}
          )
        )`;
            }
            else {
                this.templateColumns = `repeat(auto-fit,
          minmax(
            ${this.groupMinWidth},
            max(${this.groupMaxWidth}, (100% - ${this.groupMaxWidth} - (${formGroupsPerRow - 1} * ${this.columnGap}))/${formGroupsPerRow})
          )
        )`;
            }
        }
        else {
            this.templateColumns = `repeat(auto-fit, minmax(${this.groupMinWidth}, ${this.groupMaxWidth}))`;
        }
    }
    applyGridLayout() {
        this.determineGridTemplateColumns();
        this.applyAppropriateGridTemplateColumns();
    }
    applyAppropriateGridTemplateColumns() {
        const containerWidth = this.nativeElement.clientWidth;
        const containerIsExtraSmall = containerWidth < BOOTSTRAP_BREAKPOINTS.smMinimum;
        this.gridTemplateColumns = containerIsExtraSmall ? 'auto' : this.templateColumns;
    }
    toggleElementVisibility(visible) {
        const hostElementStyle = this.nativeElement.style;
        hostElementStyle.visibility = visible ? '' : 'hidden';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiFormGroupContainerComponent, selector: "si-form-group-container", inputs: { groupMinWidth: "groupMinWidth", groupMaxWidth: "groupMaxWidth", maxFormGroupsPerRow: "maxFormGroupsPerRow", columnGap: "columnGap", rowGap: "rowGap" }, host: { listeners: { "window:resize": "onResize($event)" }, properties: { "style.grid-column-gap": "this.columnGap", "style.grid-row-gap": "this.rowGap", "style.grid-template-columns": "this.gridTemplateColumns" } }, queries: [{ propertyName: "formGroups", predicate: SiFormGroupComponent }], usesOnChanges: true, ngImport: i0, template: "<ng-content></ng-content>\n", styles: [":host{display:grid}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-group-container', template: "<ng-content></ng-content>\n", styles: [":host{display:grid}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { formGroups: [{
                type: ContentChildren,
                args: [SiFormGroupComponent]
            }], groupMinWidth: [{
                type: Input
            }], groupMaxWidth: [{
                type: Input
            }], maxFormGroupsPerRow: [{
                type: Input
            }], columnGap: [{
                type: HostBinding,
                args: ['style.grid-column-gap']
            }, {
                type: Input
            }], rowGap: [{
                type: HostBinding,
                args: ['style.grid-row-gap']
            }, {
                type: Input
            }], gridTemplateColumns: [{
                type: HostBinding,
                args: ['style.grid-template-columns']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class SiFormGroupContainerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerModule, declarations: [SiFormGroupComponent,
            SiFormGroupContainerComponent], imports: [CommonModule,
            SiFormModule,
            SiResizeObserverModule,
            TranslateModule], exports: [SiFormGroupComponent,
            SiFormGroupContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerModule, imports: [CommonModule,
            SiFormModule,
            SiResizeObserverModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormGroupContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiFormGroupComponent,
                        SiFormGroupContainerComponent
                    ],
                    imports: [
                        CommonModule,
                        SiFormModule,
                        SiResizeObserverModule,
                        TranslateModule
                    ],
                    exports: [
                        SiFormGroupComponent,
                        SiFormGroupContainerComponent
                    ]
                }]
        }] });

class SiErrorHandlerService {
    constructor(siModal, toastNotificationService) {
        this.siModal = siModal;
        this.toastNotificationService = toastNotificationService;
        this.http = 'HTTP';
        this.languageTranslationPrefix = 'SISUITE.ERROR';
        this.unknownErrorKey = this.languageTranslationPrefix + '.' + 'UNKNOWN-ERROR';
        this.errorTitle = this.languageTranslationPrefix + '.' + 'TITLE';
        this.isErrorDialogOpened = false;
    }
    handleHttpError(error, useToast = false) {
        // Verify if error is not empty and earlier dialogue box is already open
        if (error && !this.isErrorDialogOpened) {
            if (KNOWN_ERROR_CODES.includes(error.status)) {
                this.showErrorAlert(this.languageTranslationPrefix + '.' + this.http + '.' + error.status.toString(), false, useToast);
            }
            else {
                if (this.inRange(error.status, 400, 500)) {
                    this.showErrorAlert(this.languageTranslationPrefix + '.' + this.http + '.' + '4xx', false, useToast);
                }
                else if (this.inRange(error.status, 500, 600)) {
                    this.showErrorAlert(this.languageTranslationPrefix + '.' + this.http + '.' + '5xx', false, useToast);
                }
                else {
                    this.showErrorAlert('', true, useToast);
                }
            }
        }
    }
    showErrorAlert(errMsgToDisplay, isUnknownError = false, useToast = false) {
        // if error is unknown, assign pre-defined translation key
        if (isUnknownError) {
            errMsgToDisplay = this.unknownErrorKey;
        }
        // show error on toast if useToast is true otherwise show in modal dialog
        if (useToast) {
            this.toastNotificationService.queueToastNotification('danger', this.errorTitle, errMsgToDisplay);
        }
        else {
            // Verify if earlier  error dialogue is already open
            if (!this.isErrorDialogOpened) {
                this.isErrorDialogOpened = true;
                this.siModal.showAlertDialog(errMsgToDisplay).subscribe((modalDialogResult) => {
                    this.onErrorDialogConfirmed(modalDialogResult);
                });
            }
        }
    }
    inRange(val, min, max) {
        return ((val - min) * (val - max) <= 0);
    }
    onErrorDialogConfirmed(modalDialogResult) {
        if (modalDialogResult === AlertDialogResult.Confirm) {
            this.isErrorDialogOpened = false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerService, deps: [{ token: i1$1.SiActionDialogService }, { token: i1$1.SiToastNotificationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.SiActionDialogService }, { type: i1$1.SiToastNotificationService }]; } });

class SiErrorHandlerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerModule, providers: [SiErrorHandlerService, SiToastNotificationService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiErrorHandlerModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [SiErrorHandlerService, SiToastNotificationService]
                }]
        }] });

class SiCorporateInformationComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCorporateInformationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiCorporateInformationComponent, selector: "si-corporate-information", ngImport: i0, template: `
  <div class="modal-header">
    <h4 class="modal-title" id="sample-modal-title">{{ 'SEC_MANAGER.ABOUT.CORPORATE_INFORMATION' | translate }}</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modalRef?.hide()">
      <span aria-hidden="true" class="element-cancel"></span>
    </button>
  </div>
  <div class="modal-body">
    <b>{{ 'SEC_MANAGER.ABOUT.HEADQUARTER_NAME' | translate }}</b><br/>
    Smart Infrastructure, Global Headquarters<br/>
    Theilerstrasse 1a<br/>
    6300 Zug<br/>
    Switzerland<br/><br/>
    <a href="mailto:web.sbt@siemens.com">web.sbt@siemens.com</a>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-primary" (click)="modalRef?.hide()">Close</button>
  </div>
  `, isInline: true, dependencies: [{ kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCorporateInformationComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'si-corporate-information',
                    template: `
  <div class="modal-header">
    <h4 class="modal-title" id="sample-modal-title">{{ 'SEC_MANAGER.ABOUT.CORPORATE_INFORMATION' | translate }}</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modalRef?.hide()">
      <span aria-hidden="true" class="element-cancel"></span>
    </button>
  </div>
  <div class="modal-body">
    <b>{{ 'SEC_MANAGER.ABOUT.HEADQUARTER_NAME' | translate }}</b><br/>
    Smart Infrastructure, Global Headquarters<br/>
    Theilerstrasse 1a<br/>
    6300 Zug<br/>
    Switzerland<br/><br/>
    <a href="mailto:web.sbt@siemens.com">web.sbt@siemens.com</a>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-primary" (click)="modalRef?.hide()">Close</button>
  </div>
  `
                }]
        }] });

class SiSecAboutComponent {
    constructor(httpClient, modalService, translateService, cd) {
        this.httpClient = httpClient;
        this.modalService = modalService;
        this.translateService = translateService;
        this.cd = cd;
        this.appName = 'Security Manager';
        this.icon = 'assets/security.svg';
        this.ossReadmeFilePath = './assets/LicenseInfo.txt';
        this.imprintLink = {
            title: 'SEC_MANAGER.ABOUT.CORPORATE_INFORMATION',
            action: () => this.showImprint()
        };
        this.privacyLink = {
            title: 'SEC_MANAGER.ABOUT.PRIVACY_NOTICE',
            href: this.translateService.instant('SEC_MANAGER.ABOUT.PRIVACY_NOTICE_LINK')
        };
        this.acceptableUsePolicyLink = {
            title: 'SEC_MANAGER.ABOUT.ACCEPTABLE_USE_POLICY',
            href: this.translateService.instant('SEC_MANAGER.ABOUT.ACCEPTABLE_USE_POLICY_LINK')
        };
        this.links = [];
        this.license = '';
    }
    ngOnInit() {
        this.sub = this.httpClient
            .get(this.ossReadmeFilePath, { responseType: 'text' })
            .subscribe(content => {
            this.license = content;
            this.cd.detectChanges();
        });
    }
    ngOnDestroy() {
        this.modalRef?.hide();
        this.sub?.unsubscribe();
    }
    showImprint() {
        this.modalRef = this.modalService.show(SiCorporateInformationComponent, {
            ignoreBackdropClick: false,
            keyboard: true,
            animated: true,
            ariaLabelledBy: this.translateService.instant('SEC_MANAGER.ABOUT.CORPORATE_INFORMATION')
        });
        if (this.modalRef.content) {
            this.modalRef.content.modalRef = this.modalRef;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutComponent, deps: [{ token: i1$2.HttpClient }, { token: i1$1.SiModalService }, { token: i3$1.TranslateService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSecAboutComponent, selector: "si-sec-about", inputs: { appName: "appName", icon: "icon", ossReadmeFilePath: "ossReadmeFilePath", subheading: "subheading", imprintLink: "imprintLink", privacyLink: "privacyLink", acceptableUsePolicyLink: "acceptableUsePolicyLink", links: "links" }, ngImport: i0, template: "<si-about\n  [icon]=\"icon\"\n  [aboutTitle]=\"'SEC_MANAGER.ABOUT.ABOUT' | translate\"\n  [licenseInfo]=\"{\n    title: 'SEC_MANAGER.ABOUT.LICENSE' | translate,\n    text: license\n  }\"\n  [appName]=\"appName\"\n  [subheading]=\"[subheading]\"\n  [imprintLink]=\"imprintLink\"\n  [privacyLink]=\"privacyLink\"\n  [acceptableUsePolicyLink]=\"acceptableUsePolicyLink\"\n  [links]=\"links\"\n  [copyrightDetails]=\"{\n    startYear: 'SEC_MANAGER.ABOUT.IMPRINT_START_YEAR' | translate,\n    lastUpdateYear: 'SEC_MANAGER.ABOUT.IMPRINT_LAST_UPDATE_YEAR' | translate\n  }\"\n></si-about>\n", dependencies: [{ kind: "component", type: i1$1.SiAboutComponent, selector: "si-about", inputs: ["aboutTitle", "licenseInfo", "icon", "iconName", "appName", "subheading", "acceptableUsePolicyLink", "imprintLink", "privacyLink", "cookieNoticeLink", "termsLink", "links", "copyrightDetails"] }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-sec-about', template: "<si-about\n  [icon]=\"icon\"\n  [aboutTitle]=\"'SEC_MANAGER.ABOUT.ABOUT' | translate\"\n  [licenseInfo]=\"{\n    title: 'SEC_MANAGER.ABOUT.LICENSE' | translate,\n    text: license\n  }\"\n  [appName]=\"appName\"\n  [subheading]=\"[subheading]\"\n  [imprintLink]=\"imprintLink\"\n  [privacyLink]=\"privacyLink\"\n  [acceptableUsePolicyLink]=\"acceptableUsePolicyLink\"\n  [links]=\"links\"\n  [copyrightDetails]=\"{\n    startYear: 'SEC_MANAGER.ABOUT.IMPRINT_START_YEAR' | translate,\n    lastUpdateYear: 'SEC_MANAGER.ABOUT.IMPRINT_LAST_UPDATE_YEAR' | translate\n  }\"\n></si-about>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: i1$1.SiModalService }, { type: i3$1.TranslateService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { appName: [{
                type: Input
            }], icon: [{
                type: Input
            }], ossReadmeFilePath: [{
                type: Input
            }], subheading: [{
                type: Input
            }], imprintLink: [{
                type: Input
            }], privacyLink: [{
                type: Input
            }], acceptableUsePolicyLink: [{
                type: Input
            }], links: [{
                type: Input
            }] } });

class SiSecAboutModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutModule, declarations: [SiCorporateInformationComponent, SiSecAboutComponent], imports: [CommonModule,
            RouterModule,
            SiAboutModule,
            SiModalModule$1,
            TranslateModule], exports: [SiSecAboutComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutModule, imports: [CommonModule,
            RouterModule,
            SiAboutModule,
            SiModalModule$1,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSecAboutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiCorporateInformationComponent, SiSecAboutComponent],
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiAboutModule,
                        SiModalModule$1,
                        TranslateModule
                    ],
                    exports: [SiSecAboutComponent],
                    providers: []
                }]
        }] });

class SimplSiShellElementModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellElementModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellElementModule, imports: [CommonModule,
            TranslateModule], exports: [SiAbstractCrudModule,
            SiAppModule,
            SiErrorHandlerModule,
            SiFormGroupContainerModule,
            SiModalModule,
            SiNotificationBarModule,
            SiSecAboutModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellElementModule, imports: [CommonModule,
            TranslateModule, SiAbstractCrudModule,
            SiAppModule,
            SiErrorHandlerModule,
            SiFormGroupContainerModule,
            SiModalModule,
            SiNotificationBarModule,
            SiSecAboutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellElementModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TranslateModule
                    ],
                    exports: [
                        SiAbstractCrudModule,
                        SiAppModule,
                        SiErrorHandlerModule,
                        SiFormGroupContainerModule,
                        SiModalModule,
                        SiNotificationBarModule,
                        SiSecAboutModule
                    ]
                }]
        }] });

// Public API Surface

/**
 * Generated bundle index. Do not edit.
 */

export { KNOWN_ERROR_CODES, LANGUAGE_TRANSLATION_PREFIX, PageCache, SiAbstractCrudComponent, SiAbstractCrudModule, SiAppComponent, SiAppLinkService, SiAppModule, SiAuthGatewayService, SiAuthGatewayServiceImpl, SiAuthGatewayServiceMock, SiConfigProvider, SiEntitySelectionDialogComponent, SiErrorHandlerModule, SiErrorHandlerService, SiFormGroupComponent, SiFormGroupContainerComponent, SiFormGroupContainerModule, SiLanguageSwitchService, SiModalModule, SiModalService, SiNotificationBarComponent, SiNotificationBarModule, SiSecAboutComponent, SiSecAboutModule, SiSecNavService, SiSecNavigationComponent, SiShellLocaleStoreService, SiShellRemoteLocaleService, SiTenantService, SimplSiShellElementModule, THEME_COOKIE_NAME, ViewMode, activitiesUrlPrefix, adaptLocale, analyticsUrlPrefix, appLoadFactory, buildTranslationResources, connectionUrlPrefix, dashboardUrlPrefix, dlmUrlPrefix, identitiesUrlPrefix, incidentsUrlPrefix, knownLanguagesMap, privilegesUrlPrefix, requestsUrlPrefix, securityManagerUrlPrefixes, selfServicesUrlPrefix, siFormCanDeactivateGuard, systemSettingsUrlPrefix, tenatsUrlPrefix, timeSchedulesUrlPrefix, uumUrlPrefix };
